import { useMemo, useCallback, useEffect, useState, useRef, memo } from "react";
import { useApolloClient, gql } from "@apollo/client";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Form } from "@formio/react";
import { Dialog, DialogContent, DialogTitle, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import {
  Trash2,
  FileText,
  GripVertical,
  Upload,
  Info,
  File,
  X,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  FileCheck2,
  Users,
  ShieldCheck,
  ClipboardList,
  Ban,
  Scale,
  Paperclip,
  Pencil,
  Copy,
  Check,
  DownloadCloud,
} from "lucide-react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import {
  buildDocumentListWithVariables,
  type DocumentListWithVariables,
  type DocumentWithVariables,
} from "./buildDocumentListWithVariables";
import { DocumentActionsMenu } from "./DocumentActionsMenu";
import {
  createDocumentRestore,
  documentJsonReplacer,
  parseDocumentRestore,
} from "./documentJsonRestore";
import type { ProductionAttachment } from "./ProductionPdfPreview";
import { ProductionPreview } from "./ProductionPreview";
import {
  ProductionSigningResult,
  type ProductionSigningResultData,
} from "./ProductionSigningResult";
import { SigneeSelector, type Signee } from "./SigneeSelector";
import { ORDER_LOCK_REASON, useOrderLock } from "./useOrderLock";
import { useGetContactOverview } from "@/api/contact/useGetContactOverview";
import { useGetExtendedContactDetails } from "@/api/contact/useGetExtendedContactDetails";
import { useLoadForm } from "@/api/docprod/useLoadForm";
import { useDownloadAttachment } from "@/api/docproducer/useDownloadAttachment";
import {
  useGetProducedDocuments,
  ProducedDocumentsTask,
} from "@/api/docproducer/useGetProducedDocuments";
import { useGetCompletedProcessTask } from "@/api/flowable/useGetCompletedProcessTask";
import {
  QueryContact,
  QueryModelPosition,
  QueryProfile,
  QuerySecurity,
} from "@/api/queries/types";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";
import { useDownloadReport } from "@/api/reports/useDownloadReport";
import { Contact, Portfolio } from "@/api/types";
import { Button } from "@/components/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/dropdown-menu";
import { LogoAnimation } from "@/components/Logo/logoAnimation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/select";
import { SelectButton } from "@/components/select-button";
import { AutoSuit } from "@/forms/classes/AutoSuit";
import { SuitStatement } from "@/forms/classes/SuitStatement";
import { OrderForm } from "@/forms/forms/orders/orderForm/OrderForm";
import { SERVICE_TYPE_LABELS } from "@/forms/forms/orders/orderForm/OrderForm.options";
import { OrderFormClean as OrderFormNew } from "@/forms/forms/orders/orderFormClean/OrderFormClean";
import { AdvisoryAgainstForm } from "@/forms/forms/quality/advisoryAgainst/AdvisoryAgainstForm";
import { KycOrgForm } from "@/forms/forms/quality/kycOrg/KycOrgForm";
import {
  CAPITAL_NEED_OPTIONS,
  NET_WEALTH_OPTIONS,
  DEBT_BURDEN_OPTIONS as ORG_DEBT_BURDEN_OPTIONS,
  ORG_INVESTMENT_HORIZON_OPTIONS,
  ORG_RISK_COMFORT_OPTIONS,
  ORG_LIQUIDITY_OPTIONS,
  ORG_INVESTMENT_GOAL_OPTIONS,
} from "@/forms/forms/quality/kycOrg/KycOrgForm.options";
import { KycPrivForm } from "@/forms/forms/quality/kycPriv/KycPrivForm";
import {
  ESG_PREF_OPTIONS,
  ESG_PREF_TYPE_OPTIONS,
  GROSS_ANNUAL_INCOME_OPTIONS,
  INCOME_STABILITY_OPTIONS,
  INVESTMENT_GOAL_OPTIONS,
  INVESTMENT_HORIZON_OPTIONS,
  KNOWLEDGE_COLUMNS,
  EXPERIENCE_COLUMNS,
  TRADING_FREQUENCY_COLUMNS,
  PERIOD_COLUMNS,
  LIQUIDITY_OPTIONS,
  MIFID_EXPERIENCE_EDUCATION_OPTIONS,
  MIFID_INSTRUMENT_ROWS,
  RISK_COMFORT_OPTIONS,
  MARKET_DROP_RESPONSE_OPTIONS,
  VALUE_DECLINE_IMPACT_OPTIONS,
} from "@/forms/forms/quality/kycPriv/KycPrivForm.options";
import { MeetingMinutesForm } from "@/forms/forms/quality/meetingMinutes/MeetingMinutesForm";
import { OpenCustomerInstructionForm } from "@/forms/forms/quality/openCustomerInstruction/OpenCustomerInstructionForm";
import { SuitabilityForm } from "@/forms/forms/quality/suitability/SuitabilityForm";
import {
  computeSuitabilityDocParams,
  calcStockPartsFromPortfolios,
} from "@/forms/forms/quality/suitability/SuitabilityForm.rules";
import { PendingOrder } from "@/forms/forms/quality/suitability/SuitabilityForm.types";
import { SuitabilityNewForm } from "@/forms/forms/quality/suitabilityNew/SuitabilityNewForm";
import { cn } from "@/lib/utils";
import {
  ContactEntry,
  DocProducerOverrides,
  useDocProducer,
  useDocProducerUI,
} from "@/providers/DocProducerProvider";
import { useKeycloak } from "@/providers/KeycloakProvider";
import { DocProducerDocument } from "@/views/docproducer";
import { FormView } from "@/views/form/form";

// ── Production process mutation (run imperatively — no form UI shown) ────────
const PRODUCTION_START_PROCESS = gql`
  mutation ProductionStartProcess($key: String, $data: Json) {
    startProcess(key: $key, data: $data) {
      formDefinition
      taskId
      processInstanceId
      data
    }
  }
`;

type ProfileMap = Record<string, string | number | boolean | null>;
export function buildProfileMap(profileData: QueryProfile[]): ProfileMap {
  const map: ProfileMap = {};
  for (const p of profileData) {
    let value: string | number | boolean | null = null;
    switch (p.primaryType ?? "") {
      case "java.lang.Boolean":
        value = p.booleanValue;
        break;
      case "java.lang.Double":
        value = p.doubleValue;
        break;
      case "java.lang.Integer":
      case "java.lang.Long":
        value = p.intValue;
        break;
      case "java.util.Date":
        value = p.dateValue;
        break;
      default:
        value = p.stringValue ?? null;
    }
    map[p.attributeKey] = value;
    const short = p.attributeKey.replace(/^contact\.[^.]+\./, "");
    if (short !== p.attributeKey) map[short] = value;
  }
  return map;
}

function parseOrderAmount(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (value === null || value === undefined || value === "") return 0;
  const normalized = String(value).replace(/\s/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function computeNoModelStockFraction(
  buyRows: Array<{
    instrumentName?: string;
    instrumentCode?: string;
    class1code?: string;
    class2code?: string;
    amountInput?: string | number;
  }>
): number {
  const filledRows = buyRows.filter(
    (r) =>
      (r.instrumentName ?? "").trim() !== "" ||
      (r.instrumentCode ?? "").trim() !== ""
  );
  const totalBuy = filledRows.reduce(
    (sum, r) => sum + parseOrderAmount(r.amountInput),
    0
  );
  if (totalBuy <= 0) return 0;

  const stockBuy = filledRows
    .filter((r) => {
      const class1 = String(r.class1code ?? "").toUpperCase();
      const class2 = String(r.class2code ?? "").toUpperCase();
      return class1 === "E" || class2 === "S" || class2 === "EF";
    })
    .reduce((sum, r) => sum + parseOrderAmount(r.amountInput), 0);

  return stockBuy / totalBuy;
}

function findSecurityForOrderRow(
  securities: QuerySecurity[] | undefined,
  instrumentCode: string,
  instrumentName: string
): QuerySecurity | null {
  const list = securities ?? [];
  const code = instrumentCode.trim().toUpperCase();
  const name = instrumentName.trim().toUpperCase();

  if (code) {
    const byCode = list.find(
      (s) =>
        String(s.security_code ?? "")
          .trim()
          .toUpperCase() === code
    );
    if (byCode) return byCode;
  }

  if (name) {
    const byName = list.find(
      (s) =>
        String(s.name ?? "")
          .trim()
          .toUpperCase() === name
    );
    if (byName) return byName;
  }

  return null;
}

function getProfileDocumentType(
  documentType?: string
): "mifidForm" | "amlForm" {
  const normalized = (documentType ?? "").toLowerCase();
  return normalized.includes("mifid") || normalized.includes("investor")
    ? "mifidForm"
    : "amlForm";
}

function getProfileDocumentId(
  documentType: string | undefined,
  fileName: string | undefined,
  timestamp: string
): string {
  const sourceName = fileName || documentType || "profile-document";
  const safeName = sourceName
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return `${getProfileDocumentType(documentType)}_${
    safeName || "document"
  }_${timestamp}`;
}

interface UploadedDocument {
  file: File;
  stream: string;
}

interface DocProducerContentProps {
  selectedContact?: QueryContact | null;
}
interface DocProducerData {
  formKey: string;
  title: string;
  toProduction: boolean;
  extraValues?: Record<string, unknown>;
  inpData?: Record<string, unknown>;
}
type ProductionContext = DocumentListWithVariables & {
  afterSign: Record<string, unknown> | null;
  portfolios: Portfolio[];
};

interface NewOrderDocument {
  id: string;
  docType: string;
  data?: {
    newData: {
      dpPortSelector: {
        name: string;
      };
      dpModelType: {
        label: string;
      };
      dpAmountToInvest: number;
      dpOneOffAmount: number;
    };
  };
}

// ── Per-docType visual config ────────────────────────────────────────────────
const DOC_TYPE_CONFIG: Record<
  string,
  {
    icon: React.ElementType;
    iconBg: string;
    iconColor: string;
    borderColor: string;
  }
> = {
  order: {
    icon: TrendingUp,
    iconBg: "bg-blue-50 dark:bg-blue-950/40",
    iconColor: "text-blue-600 dark:text-blue-400",
    borderColor: "border-l-blue-500",
  },
  meetingMinutes: {
    icon: Users,
    iconBg: "bg-violet-50 dark:bg-violet-950/40",
    iconColor: "text-violet-600 dark:text-violet-400",
    borderColor: "border-l-violet-500",
  },
  suitabilityStatement: {
    icon: FileCheck2,
    iconBg: "bg-emerald-50 dark:bg-emerald-950/40",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    borderColor: "border-l-emerald-500",
  },
  docSuitabilityNew: {
    icon: FileCheck2,
    iconBg: "bg-emerald-50 dark:bg-emerald-950/40",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    borderColor: "border-l-emerald-500",
  },
  quality: {
    icon: ShieldCheck,
    iconBg: "bg-amber-50 dark:bg-amber-950/40",
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-l-amber-500",
  },
  customerAgreement: {
    icon: ShieldCheck,
    iconBg: "bg-amber-50 dark:bg-amber-950/40",
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-l-amber-500",
  },
  openCustomerInstruction: {
    icon: ClipboardList,
    iconBg: "bg-sky-50 dark:bg-sky-950/40",
    iconColor: "text-sky-600 dark:text-sky-400",
    borderColor: "border-l-sky-500",
  },
  advisoryAgainst: {
    icon: Ban,
    iconBg: "bg-rose-50 dark:bg-rose-950/40",
    iconColor: "text-rose-600 dark:text-rose-400",
    borderColor: "border-l-rose-500",
  },
  uploaded: {
    icon: Paperclip,
    iconBg: "bg-gray-100 dark:bg-gray-800",
    iconColor: "text-gray-500 dark:text-gray-400",
    borderColor: "border-l-gray-400",
  },
  documentFromSecurity: {
    icon: FileCheck2,
    iconBg: "bg-teal-50 dark:bg-teal-950/40",
    iconColor: "text-teal-600 dark:text-teal-400",
    borderColor: "border-l-teal-500",
  },
  mifidForm: {
    icon: TrendingUp,
    iconBg: "bg-violet-50 dark:bg-violet-950/40",
    iconColor: "text-violet-600 dark:text-violet-400",
    borderColor: "border-l-violet-500",
  },
  amlForm: {
    icon: ShieldCheck,
    iconBg: "bg-amber-50 dark:bg-amber-950/40",
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-l-amber-500",
  },
};

const DEFAULT_DOC_CONFIG = {
  icon: FileText,
  iconBg: "bg-gray-100 dark:bg-gray-800",
  iconColor: "text-gray-500 dark:text-gray-400",
  borderColor: "border-l-gray-400",
};

// Task attachments are strings in some queries and objects in others (e.g. Compliance tasks).
const getAttachmentName = (attachment: unknown): string => {
  if (typeof attachment === "string") return attachment;
  if (attachment && typeof attachment === "object") {
    const record = attachment as Record<string, unknown>;
    for (const key of ["name", "fileName", "filename", "title"]) {
      if (typeof record[key] === "string") return record[key] as string;
    }
  }
  return "";
};

// Parses signing links from a task description (HTML format from Flowable)
const parseSigningInfo = (
  description: string | null | undefined
): Array<{ name: string; contactId: string; link: string; status: string }> => {
  if (!description) return [];
  const signers: Array<{
    name: string;
    contactId: string;
    link: string;
    status: string;
  }> = [];
  const regex =
    /<li>([^(]+)\(([^)]+)\):[^<]*<a href='([^']+)'[^>]*>.*?Status:\s*(\w+)<\/li>/gi;
  let match;
  while ((match = regex.exec(description)) !== null) {
    signers.push({
      name: match[1].trim(),
      contactId: match[2].trim(),
      link: match[3].trim(),
      status: match[4].trim(),
    });
  }
  return signers;
};

// Memoized document row shared by every document type.
const DocumentItem = memo(
  ({
    doc,
    onDelete,
    onEdit,
    disabledReason,
  }: {
    doc: DocProducerDocument;
    onDelete: (doc: DocProducerDocument) => void;
    onEdit?: (doc: DocProducerDocument) => void;
    disabledReason?: string;
  }) => {
    type MetaItem = {
      label?: string;
      value: string;
      mono?: boolean;
      amount?: boolean;
    };

    const renderMeta = (items: MetaItem[]) => {
      return (
        <dl className="flex min-w-0 flex-wrap gap-y-2">
          {items.map((item, i) => (
            <div
              key={`${item.label ?? "value"}-${i}`}
              className="min-w-0 max-w-full border-l border-gray-100 px-3 first:border-l-0 first:pl-0 dark:border-gray-800"
            >
              {item.label && (
                <dt className="shrink-0 text-[10px] text-gray-500 dark:text-gray-400">
                  {item.label}
                </dt>
              )}
              <dd
                className={cn(
                  "mt-0.5 max-w-full truncate text-xs font-medium text-gray-900 dark:text-gray-100",
                  item.mono && "font-mono",
                  item.amount && "tabular-nums"
                )}
                title={item.value}
              >
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      );
    };

    // ── Resolve title & meta pills per docType ───────────────────────────────

    let title = doc.processName || doc.docType;
    let subtitle = FORM_TYPE_LABELS[doc.docType] ?? "";
    let metaItems: (MetaItem | null)[] = [];

    if (doc.docType === "uploaded") {
      const u = doc as typeof doc & { data?: { file?: File }; desc?: string };
      title = "Vedlegg";
      metaItems = [
        u.desc ? { label: "Fil", value: u.desc, mono: true } : null,
        u.data?.file
          ? {
              label: "Størrelse",
              value:
                u.data.file.size > 1024 * 1024
                  ? `${(u.data.file.size / (1024 * 1024)).toFixed(2)} MB`
                  : `${(u.data.file.size / 1024).toFixed(2)} KB`,
            }
          : null,
      ];
    } else if (doc.docType === "order") {
      const raw = doc.data as Record<string, unknown> | undefined;
      const nd = (raw?.newData as Record<string, unknown> | undefined) ?? raw;
      const portfolioName =
        (nd?._portfolioName as string) ||
        (raw?.dpPortSelector as { name?: string } | undefined)?.name;
      const modelType =
        (nd?.dpModelType as string) ||
        (raw?.dpModelType as { label?: string } | undefined)?.label;
      const modelName = nd?.dpModelTypeName as string | undefined;
      const monthly =
        (nd?.dpAmountToInvest as number) ?? (raw?.dpAmountToInvest as number);
      const oneOff =
        (nd?.dpOnOffAmount as number) ?? (raw?.dpOneOffAmount as number);
      const withdrawal =
        (nd?.dpWithdrawal as number) ?? (raw?.withdrawal as number);
      const portId =
        (nd?.dpPortfolioId as string) ?? (raw?.portfolioId as string);
      const feeType = (nd?.dpFeeType as string) ?? (raw?.feeType as string);
      const serviceType = SERVICE_TYPE_LABELS[nd?.dpServiceType as string];

      title =
        portId === "NEW"
          ? "Ny portefølje"
          : portfolioName || "Eksisterende portefølje";
      subtitle = serviceType ?? "";

      metaItems = [
        modelName || modelType
          ? { label: "Modell", value: modelName || modelType || "" }
          : null,
        feeType === "all"
          ? { label: "Avtale", value: "Alle tjenester" }
          : feeType === "onlyFund"
          ? { label: "Avtale", value: "Kun fond" }
          : null,
        monthly
          ? {
              label: "Investering",
              value: `${monthly.toLocaleString("nb-NO")} kr`,
              amount: true,
            }
          : null,
        oneOff
          ? {
              label: "Engangsbeløp",
              value: `${oneOff.toLocaleString("nb-NO")} kr`,
              amount: true,
            }
          : null,
        withdrawal
          ? {
              label: "Uttak",
              value: `${withdrawal.toLocaleString("nb-NO")} kr`,
              amount: true,
            }
          : null,
      ];
    } else if (doc.docType === "meetingMinutes") {
      title = "Møtereferat";
      const nd = (doc.data as Record<string, unknown> | undefined)?.newData as
        | Record<string, unknown>
        | undefined;
      const rawDate = nd?.dpdocRefMeetDate as string | undefined;
      metaItems = [
        rawDate
          ? {
              label: "Dato",
              value: rawDate.replace("T", " ").slice(0, 16),
              mono: true,
            }
          : null,
        nd?.dpdocRefMeetPlace
          ? { label: "Sted", value: nd.dpdocRefMeetPlace as string }
          : null,
        nd?.dpDocRefMeetPart
          ? { label: "Deltakere", value: nd.dpDocRefMeetPart as string }
          : null,
      ];
    } else if (
      doc.docType === "suitabilityStatement" ||
      doc.docType === "docSuitabilityNew"
    ) {
      title =
        doc.docType === "suitabilityStatement"
          ? "Egnethetsattest"
          : "Suitability";
      const nd = (doc.data as Record<string, unknown> | undefined)?.newData as
        | Record<string, unknown>
        | undefined;
      metaItems = [
        nd?.dpInvTime
          ? { label: "Tidshorisont", value: nd.dpInvTime as string }
          : null,
        nd?.dpInitStockPart
          ? { label: "Aksjeandel før", value: `${nd.dpInitStockPart} %` }
          : null,
        nd?.dpFinStockPart
          ? { label: "Aksjeandel etter", value: `${nd.dpFinStockPart} %` }
          : null,
      ];
    } else if (
      doc.docType === "quality" ||
      doc.docType === "customerAgreement"
    ) {
      title = doc.processName || "Kvalitetsdokument";
    }

    const cfg = DOC_TYPE_CONFIG[doc.docType] ?? DEFAULT_DOC_CONFIG;
    const DocIcon = cfg.icon;
    const visibleMeta = metaItems.filter((item): item is MetaItem => !!item);
    const information = [
      ...visibleMeta.filter((item) => item.amount),
      ...visibleMeta.filter((item) => !item.amount),
    ];
    const typeLabel =
      doc.docType === "order"
        ? "Ordre"
        : doc.docType === "meetingMinutes"
        ? "Notat"
        : doc.docType === "uploaded"
        ? "Vedlegg"
        : DOC_TYPE_CONFIG[doc.docType]
        ? "Skjema"
        : "Dokument";

    return (
      <article
        aria-label={title}
        className="grid grid-cols-[minmax(0,1fr)_auto_2.25rem] items-center gap-x-3 gap-y-2 px-4 py-3 transition-colors hover:bg-gray-50/70 dark:hover:bg-gray-800/30 lg:grid-cols-[minmax(12rem,1.2fr)_6rem_minmax(0,2fr)_2.25rem]"
      >
        <div className="col-start-1 row-start-1 flex min-w-0 items-center gap-3">
          <div
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-black/5 dark:ring-white/10",
              cfg.iconBg
            )}
          >
            <DocIcon size={18} aria-hidden="true" className={cfg.iconColor} />
          </div>
          <div className="min-w-0 flex-1">
            <p
              title={title}
              className="truncate text-[13px] font-semibold leading-tight text-gray-900 dark:text-gray-100"
            >
              {title}
            </p>
            {subtitle && subtitle !== title && (
              <p
                title={subtitle}
                className="mt-1 truncate text-[11px] text-gray-500 dark:text-gray-400"
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
        <div className="col-start-2 row-start-1 min-w-0">
          <span
            className={cn(
              "inline-flex max-w-full truncate rounded-md px-2 py-1 text-[11px] font-medium",
              cfg.iconBg,
              cfg.iconColor
            )}
          >
            {typeLabel}
          </span>
        </div>
        {information.length > 0 && (
          <div className="col-span-3 row-start-2 min-w-0 pl-[3.25rem] lg:col-span-1 lg:col-start-3 lg:row-start-1 lg:pl-0">
            {renderMeta(information)}
          </div>
        )}
        <div className="col-start-3 row-start-1 lg:col-start-4">
          <DocumentActionsMenu
            title={title}
            disabledReason={disabledReason}
            onEdit={
              ["order", "meetingMinutes", "openCustomerInstruction"].includes(
                doc.docType
              ) && onEdit
                ? () => onEdit(doc)
                : undefined
            }
            editLabel={
              doc.docType === "order" ? "Rediger ordre" : "Rediger dokument"
            }
            onDelete={() => onDelete(doc)}
          />
        </div>
      </article>
    );
  }
);

DocumentItem.displayName = "DocumentItem";

const SortableDocumentItem = ({
  doc,
  onDelete,
  onEdit,
  disabledReason,
}: {
  doc: DocProducerDocument;
  onDelete: (doc: DocProducerDocument) => void;
  onEdit: (doc: DocProducerDocument) => void;
  disabledReason?: string;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: doc.id });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "relative border-b border-gray-100 bg-white pl-7 last:border-b-0 dark:border-gray-800 dark:bg-gray-900",
        isDragging && "opacity-0"
      )}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Endre rekkefølge for ${doc.desc || doc.processName}`}
        title="Dra for å endre rekkefølge"
        className="absolute left-2 top-5 flex size-6 touch-none items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:cursor-grabbing dark:hover:bg-gray-800 dark:hover:text-gray-200 cursor-grab"
      >
        <GripVertical size={16} aria-hidden="true" />
      </button>
      <DocumentItem
        doc={doc}
        onDelete={onDelete}
        onEdit={onEdit}
        disabledReason={disabledReason}
      />
    </li>
  );
};

// Generic loader for any Formio form fetched via useLoadForm.
// Extracted as its own component so hooks can be called unconditionally.
interface LoadFormRunnerProps {
  formId: number;
  contactValues: ContactEntry;
  securities: QuerySecurity[];
  profileData: QueryProfile[];
  onDocumentCreated: (doc: DocProducerDocument) => void;
  onClose: () => void;
}

const getFeeType = (tagsAsSet: string[]) => {
  const feeType = tagsAsSet.find((tag) => tag.startsWith("Forvaltningsavtale"));
  switch (feeType) {
    case "Forvaltningsavtale-Aktiv":
      return "all";
    case "Forvaltningsavtale-Komplett":
      return "all";
    case "Forvaltningsavtale-Kun fond":
      return "only fund";
    default:
      return "";
  }
};

const LoadFormRunner = ({
  formId,
  contactValues,
  securities,
  profileData,
  onDocumentCreated,
  onClose,
}: LoadFormRunnerProps) => {
  const { data, loading } = useLoadForm(formId);

  const formSchema = useMemo(
    () =>
      data?.loadForm?.formDefinition
        ? { ...JSON.parse(data.loadForm.formDefinition), action: "" }
        : null,
    [data?.loadForm?.formDefinition]
  );

  // Pre-populate submission with context data derived from the contact
  const submission = useMemo(
    () => ({
      data: {
        contact: contactValues.contact,
        securities: securities,
        profile: profileData,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contactValues.contact?.id]
  );

  if (loading)
    return (
      <div className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <LogoAnimation size={20} />
        Loading form…
      </div>
    );
  if (!formSchema)
    return (
      <div className="p-4 text-sm text-muted-foreground">No form data.</div>
    );

  return (
    <div className="h-full overflow-auto p-4">
      <Form
        form={formSchema}
        submission={submission}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formReady={(formioInstance: any) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formioInstance.on("submit", (sub: any) => {
            onDocumentCreated({
              id: `form_${formId}_${new Date().toISOString()}`,
              docType: "order",
              processName: data?.loadForm?.formName ?? "Form",
              desc: data?.loadForm?.formName ?? "Form",
              data: { ...submission.data, ...sub.data },
            });
            formioInstance.emit("submitDone", sub);
            onClose();
          });
        }}
      />
    </div>
  );
};

const FORM_TYPE_LABELS: Record<string, string> = {
  order: "Ordre",
  meetingMinutes: "Møtereferat",
  suitabilityStatement: "Egnethetsvurdering",
  docSuitabilityNew: "Egnethetsvurdering",
  customerAgreement: "Kundeavtale",
  openCustomerInstruction: "Åpen kundeinstruks",
  advisoryAgainst: "Frarådning",
  lei: "LEI Blankett",
  docleiOrder: "LEI Blankett",
  uploaded: "Vedlegg",
  documentFromSecurity: "Dokument fra kundeprofil",
  mifidForm: "MIFID-profil",
  amlForm: "AML-profil",
};

function formatVariableValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value, null, 2) ?? "";
}

const FormPreviewItemContent = ({
  document,
  isSelected,
  onSelect,
  listeners,
}: {
  document: DocumentWithVariables;
  isSelected: boolean;
  onSelect: () => void;
  listeners?: Record<string, unknown>;
}) => (
  <button
    type="button"
    onClick={onSelect}
    className={cn(
      "flex w-full items-center gap-2 border-l-2 px-4 py-3 text-left transition-colors",
      isSelected
        ? "border-l-primary bg-primary/5 dark:bg-primary/10"
        : "border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60"
    )}
  >
    <div
      {...listeners}
      onClick={(event) => event.stopPropagation()}
      className="flex-shrink-0 cursor-grab text-gray-300 active:cursor-grabbing hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400"
      title="Dra for å endre rekkefølge"
    >
      <GripVertical size={13} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-xs font-medium text-gray-900 dark:text-gray-100">
        {FORM_TYPE_LABELS[document.type] ?? document.type}
      </p>
      <p className="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">
        {Object.keys(document.data).length} variabler
      </p>
    </div>
  </button>
);

const SortableFormPreviewItem = ({
  document,
  isSelected,
  onSelect,
}: {
  document: DocumentWithVariables;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: document.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
      }}
      {...attributes}
    >
      <FormPreviewItemContent
        document={document}
        isSelected={isSelected}
        onSelect={onSelect}
        listeners={listeners}
      />
    </div>
  );
};

const FormsPreviewView = ({
  documents,
  onBack,
  onProduce,
}: {
  documents: DocumentWithVariables[];
  onBack: () => void;
  onProduce: (documents: DocumentWithVariables[]) => void;
}) => {
  const [orderedDocuments, setOrderedDocuments] = useState(() => documents);
  const [selectedId, setSelectedId] = useState<string | null>(
    documents[0]?.id ?? null
  );
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const selected = orderedDocuments.find(
    (document) => document.id === selectedId
  );
  const activeDragDocument = activeDragId
    ? orderedDocuments.find((document) => document.id === activeDragId)
    : null;

  const previewSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => {
    setOrderedDocuments(documents);
    setSelectedId((currentId) =>
      documents.some((document) => document.id === currentId)
        ? currentId
        : documents[0]?.id ?? null
    );
  }, [documents]);

  const handlePreviewDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = orderedDocuments.findIndex(
        (document) => document.id === String(active.id)
      );
      const newIndex = orderedDocuments.findIndex(
        (document) => document.id === String(over.id)
      );
      if (oldIndex >= 0 && newIndex >= 0) {
        setOrderedDocuments((currentDocuments) =>
          arrayMove(currentDocuments, oldIndex, newIndex)
        );
      }
    }
    setActiveDragId(null);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1">
        <aside className="w-80 flex-shrink-0 border-r border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Skjemaer i produksjonen
            </span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              {documents.length}
            </span>
          </div>
          <div className="overflow-y-auto py-1">
            <DndContext
              sensors={previewSensors}
              collisionDetection={closestCenter}
              onDragStart={(event: DragStartEvent) =>
                setActiveDragId(String(event.active.id))
              }
              onDragEnd={handlePreviewDragEnd}
              onDragCancel={() => setActiveDragId(null)}
            >
              <SortableContext
                items={orderedDocuments.map((document) => document.id)}
                strategy={verticalListSortingStrategy}
              >
                {orderedDocuments.map((document) => (
                  <SortableFormPreviewItem
                    key={document.id}
                    document={document}
                    isSelected={document.id === selectedId}
                    onSelect={() => setSelectedId(document.id)}
                  />
                ))}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragDocument ? (
                  <div className="w-80 max-w-[calc(100vw-2rem)] rounded-md bg-background shadow-xl">
                    <FormPreviewItemContent
                      document={activeDragDocument}
                      isSelected={activeDragDocument.id === selectedId}
                      onSelect={() => undefined}
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        </aside>
        <section className="flex min-w-0 flex-1 flex-col bg-gray-50/40 dark:bg-gray-900/30">
          {selected ? (
            <>
              <div className="border-b border-gray-200 bg-white px-5 py-3 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {FORM_TYPE_LABELS[selected.type] ?? selected.type}
                </p>
                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                  Variabler sendt til produksjon
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                <div className="max-w-3xl divide-y divide-gray-100 dark:divide-gray-800">
                  {Object.entries(selected.data).map(([key, value]) => (
                    <div
                      key={key}
                      className="grid gap-1 py-2 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-4"
                    >
                      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        {key}
                      </span>
                      <pre className="whitespace-pre-wrap break-words font-mono text-xs text-gray-800 dark:text-gray-200">
                        {formatVariableValue(value)}
                      </pre>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-gray-400 dark:text-gray-500">
              Ingen skjemaer er valgt for produksjon.
            </div>
          )}
        </section>
      </div>
      <div className="flex flex-shrink-0 items-center justify-between border-t border-gray-200 bg-white px-5 py-3 dark:border-gray-700 dark:bg-gray-900">
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {documents.length} {documents.length === 1 ? "skjema" : "skjemaer"}{" "}
          klare
        </span>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onBack}>
            Tilbake
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => onProduce(orderedDocuments)}
          >
            Produser
          </Button>
        </div>
      </div>
    </div>
  );
};

// ProductionListView component
const DOC_TYPE_LABELS: Record<string, string> = {
  order: "Ordre",
  accountAgreement: "Kontoinngåelse",
  managementAgreement: "Forvaltningsavtale",
  modelAgreement: "Modellmandat",
  terms: "Vilkår",
  oneOff: "Engangsinnbetaling",
  withdrawal: "Uttak",
  manualOrder: "Ordreblankett",
  savingsOrder: "Månedlig sparing",
  meetingMinutes: "Møtereferat",
  suitabilityStatement: "Egnethetsattest",
  docSuitabilityNew: "Egnethetsattest",
  customerAgreement: "Kundeavtale",
  openCustomerInstruction: "Åpen kundeinstruks",
  advisoryAgainst: "Frarådning",
  lei: "LEI Blankett",
  docleiOrder: "LEI Blankett",
  uploaded: "Vedlegg",
  documentFromSecurity: "Dokument (kundeprofil)",
  mifidForm: "MIFID-profil",
  amlForm: "AML-profil",
};

const SortableProductionItem = ({
  pd,
  isSelected,
  onSelect,
}: {
  pd: DocumentWithVariables;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pd.id + "_" + pd.type });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <ProductionItemContent
        pd={pd}
        isSelected={isSelected}
        onSelect={onSelect}
        listeners={listeners}
      />
    </div>
  );
};

const ProductionItemContent = ({
  pd,
  isSelected,
  onSelect,
  listeners,
}: {
  pd: DocumentWithVariables;
  isSelected: boolean;
  onSelect: () => void;
  listeners?: Record<string, unknown>;
}) => {
  const cfg = DOC_TYPE_CONFIG[pd.type] ?? DEFAULT_DOC_CONFIG;
  const DocIcon = cfg.icon;
  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2.5 text-left border-l-2 transition-colors",
        isSelected
          ? "border-l-primary bg-primary/5 dark:bg-primary/10"
          : "border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60"
      )}
    >
      <div
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="cursor-grab active:cursor-grabbing text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 flex-shrink-0"
      >
        <GripVertical size={13} />
      </div>
      <div
        className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          cfg.iconBg
        )}
      >
        <DocIcon size={14} className={cfg.iconColor} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate leading-tight">
          {DOC_TYPE_LABELS[pd.type] ?? pd.type}
        </p>
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">
          {pd.id}
        </p>
      </div>
      <span className="flex-shrink-0 text-[10px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 rounded px-1.5 py-0.5 leading-none">
        {Object.keys(pd.data).length}
      </span>
    </button>
  );
};

SortableProductionItem.displayName = "SortableProductionItem";

const ProductionListView = ({
  productionDocList,
  onProduce,
  actionLabel = "Produser",
  kundeprofil,
  signees,
  defaultSignee,
}: {
  productionDocList: DocumentWithVariables[];
  kundeprofil: QueryProfile[];
  onProduce: (
    selectedSignees: Signee[],
    orderedList: DocumentWithVariables[],
    kundeprofil: QueryProfile[]
  ) => void;
  actionLabel?: string;
  signees?: Signee[];
  defaultSignee?: Signee;
}) => {
  const [orderedList, setOrderedList] = useState(() => productionDocList);
  const pdUid = (pd: DocumentWithVariables) => pd.id + "_" + pd.type;
  const [selectedId, setSelectedId] = useState<string | null>(
    productionDocList.length > 0 ? pdUid(productionDocList[0]) : null
  );
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const selected = orderedList.find((pd) => pdUid(pd) === selectedId) ?? null;
  const activeDragDocument = activeDragId
    ? orderedList.find((pd) => pdUid(pd) === activeDragId) ?? null
    : null;

  const [selectedSignees, setSelectedSignees] = useState<Signee[]>(() =>
    defaultSignee ? [defaultSignee] : []
  );

  const listSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleListDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = orderedList.findIndex(
        (pd) => pdUid(pd) === String(active.id)
      );
      const newIndex = orderedList.findIndex(
        (pd) => pdUid(pd) === String(over.id)
      );
      if (oldIndex >= 0 && newIndex >= 0) {
        setOrderedList((prev) => arrayMove(prev, oldIndex, newIndex));
      }
    }
    setActiveDragId(null);
  };

  const handleListDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  // Group params into key-value pairs, skipping empty values
  const paramEntries = selected
    ? Object.entries(selected.data).filter(
        ([, v]) => v !== null && v !== undefined && String(v).trim() !== ""
      )
    : [];

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left sidebar — document list */}
        <div className="w-96 flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-gray-700">
          {/* Sidebar header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Documents
            </span>
            <span className="text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-full px-2 py-0.5 leading-none">
              {productionDocList.length}
            </span>
          </div>
          {/* List */}
          <div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto py-1">
            <DndContext
              sensors={listSensors}
              collisionDetection={closestCenter}
              onDragStart={handleListDragStart}
              onDragEnd={handleListDragEnd}
              onDragCancel={() => setActiveDragId(null)}
            >
              <SortableContext
                items={orderedList.map((pd) => pdUid(pd))}
                strategy={verticalListSortingStrategy}
              >
                {orderedList.map((pd) => (
                  <SortableProductionItem
                    key={pdUid(pd)}
                    pd={pd}
                    isSelected={pdUid(pd) === selectedId}
                    onSelect={() => setSelectedId(pdUid(pd))}
                  />
                ))}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragDocument ? (
                  <div className="w-96 max-w-[calc(100vw-2rem)] rounded-md bg-background shadow-xl">
                    <ProductionItemContent
                      pd={activeDragDocument}
                      isSelected={pdUid(activeDragDocument) === selectedId}
                      onSelect={() => undefined}
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>

        {/* Signees column — dedicated list, one row per person */}
        {signees !== undefined && (
          <SigneeSelector
            signees={signees}
            selectedSignees={selectedSignees}
            onChange={setSelectedSignees}
          />
        )}

        {/* Right — parameter inspector */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0 bg-gray-50/40 dark:bg-gray-900/30">
          {selected ? (
            <>
              {/* Inspector header */}
              <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-900">
                {(() => {
                  const cfg =
                    DOC_TYPE_CONFIG[selected.type] ?? DEFAULT_DOC_CONFIG;
                  const DocIcon = cfg.icon;
                  return (
                    <div
                      className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                        cfg.iconBg
                      )}
                    >
                      <DocIcon size={16} className={cfg.iconColor} />
                    </div>
                  );
                })()}
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                    {DOC_TYPE_LABELS[selected.type] ?? selected.type}
                  </p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500">
                    {selected.id} &middot; {paramEntries.length} parameters
                  </p>
                </div>
              </div>

              {/* Param list — compact, stacked one below another */}
              <div className="flex-1 overflow-y-auto p-5">
                {paramEntries.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    No parameters
                  </p>
                ) : (
                  <div className="max-w-md flex flex-col divide-y divide-gray-100 dark:divide-gray-800">
                    {paramEntries.map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-baseline justify-between gap-3 py-1.5"
                      >
                        <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide shrink-0">
                          {key}
                        </span>
                        <span className="text-xs font-mono text-gray-800 dark:text-gray-200 break-all text-right">
                          {key === "stream"
                            ? formatVariableValue(value).slice(0, 50)
                            : formatVariableValue(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <FileText
                  size={22}
                  className="text-gray-400 dark:text-gray-500"
                  strokeWidth={1.5}
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Select a document
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  Click a document on the left to inspect its parameters
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom action row */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {productionDocList.length}{" "}
          {productionDocList.length === 1 ? "document" : "documents"} ready
        </span>
        <Button
          variant="default"
          size="sm"
          disabled={signees !== undefined && selectedSignees.length === 0}
          onClick={() => onProduce(selectedSignees, orderedList, kundeprofil)}
        >
          {actionLabel}
        </Button>
      </div>
    </div>
  );
};

const COMPLIANCE_STEPS = [
  { label: "Sendt inn", state: "done" },
  { label: "Compliance-godkjenning", state: "current" },
  { label: "Signering", state: "upcoming" },
] as const;

const ComplianceHoldView = ({
  title,
  documents,
  onClose,
}: {
  title: string;
  documents: string[];
  onClose: () => void;
}) => (
  <div className="flex h-full items-start justify-center overflow-y-auto p-8">
    <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-amber-200/70 bg-white shadow-sm dark:border-amber-900/50 dark:bg-gray-900">
      <div className="flex flex-col items-center gap-4 bg-gradient-to-b from-amber-50 to-transparent px-8 pb-6 pt-10 text-center dark:from-amber-950/30">
        <div className="relative">
          <span className="absolute inset-0 animate-ping rounded-full bg-amber-300/40 dark:bg-amber-500/20" />
          <div className="relative flex size-16 items-center justify-center rounded-full bg-amber-100 ring-8 ring-amber-50 dark:bg-amber-900/40 dark:ring-amber-950/40">
            <ShieldCheck
              className="size-8 text-amber-600 dark:text-amber-400"
              strokeWidth={1.75}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Produksjonen er stoppet til Compliance-godkjenning
          </h3>
          <p className="mx-auto max-w-md text-sm text-gray-500 dark:text-gray-400">
            Dokumentene er sendt inn, men må godkjennes av Compliance før
            produksjonen kan fortsette.
          </p>
        </div>
        {title && (
          <span className="inline-flex max-w-full items-center truncate rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-medium text-amber-800 dark:border-amber-800 dark:bg-gray-900 dark:text-amber-300">
            {title}
          </span>
        )}
      </div>

      <ol className="flex items-start px-8 py-6">
        {COMPLIANCE_STEPS.map((step, index) => (
          <li
            key={step.label}
            className="relative flex flex-1 flex-col items-center gap-2 text-center"
          >
            {index > 0 && (
              <span
                aria-hidden="true"
                className={cn(
                  "absolute left-[-50%] top-3.5 h-px w-full",
                  COMPLIANCE_STEPS[index - 1].state === "done"
                    ? "bg-emerald-300 dark:bg-emerald-700"
                    : "bg-gray-200 dark:bg-gray-700"
                )}
              />
            )}
            <span
              className={cn(
                "relative z-10 flex size-7 items-center justify-center rounded-full border-2 text-[11px] font-bold",
                step.state === "done" &&
                  "border-emerald-500 bg-emerald-500 text-white",
                step.state === "current" &&
                  "border-amber-500 bg-amber-50 dark:bg-amber-950",
                step.state === "upcoming" &&
                  "border-gray-200 bg-white text-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-600"
              )}
            >
              {step.state === "done" ? (
                <Check size={14} strokeWidth={3} />
              ) : step.state === "current" ? (
                <span className="size-2 animate-pulse rounded-full bg-amber-500" />
              ) : (
                index + 1
              )}
            </span>
            <span
              className={cn(
                "text-[11px] font-medium leading-tight",
                step.state === "upcoming"
                  ? "text-gray-400 dark:text-gray-500"
                  : "text-gray-800 dark:text-gray-200"
              )}
            >
              {step.label}
            </span>
          </li>
        ))}
      </ol>

      {documents.length > 0 && (
        <div className="border-t border-gray-100 px-6 py-4 dark:border-gray-800">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
            Dokumenter som venter ({documents.length})
          </p>
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {documents.map((name, index) => (
              <li
                key={`${name}-${index}`}
                className="flex items-center gap-3 py-2"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-[9px] font-bold tracking-tight text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  {name.split(".").pop()?.toUpperCase() ?? "FIL"}
                </span>
                <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-800 dark:text-gray-200">
                  {name}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end border-t border-gray-100 bg-gray-50/60 px-6 py-3 dark:border-gray-800 dark:bg-gray-900/60">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Lukk
        </Button>
      </div>
    </div>
  </div>
);

export const DocProducerContent = ({
  selectedContact,
}: DocProducerContentProps) => {
  const apolloClient = useApolloClient();
  const { state, dispatch } = useDocProducer();
  const [showForm, setShowForm] = useState(false);
  const [activeLoadFormId, setActiveLoadFormId] = useState<number | null>(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showOrderFormNew, setShowOrderFormNew] = useState(false);
  const [editingOrderDoc, setEditingOrderDoc] =
    useState<DocProducerDocument | null>(null);
  const [editingQualityDoc, setEditingQualityDoc] =
    useState<DocProducerDocument | null>(null);
  const [activeQualityForm, setActiveQualityForm] = useState<
    | "meetingMinutes"
    | "suitability"
    | "suitabilityNew"
    | "kycPriv"
    | "kycOrg"
    | "openCustomerInstruction"
    | "advisoryAgainst"
    | null
  >(null);
  const [showUploader, setShowUploader] = useState(false);
  const [productionDocList, setProductionDocList] = useState<
    DocumentWithVariables[]
  >([]);
  const [kundeprofil, setKundeprofil] = useState<QueryProfile[]>([]);
  const [standardParams, setStandardParams] = useState<Record<string, string>>(
    {}
  );
  const [previewDocuments, setPreviewDocuments] = useState<
    DocumentWithVariables[]
  >([]);
  const [showFormsPreview, setShowFormsPreview] = useState(false);
  const [showProductionList, setShowProductionList] = useState(false);
  const [complianceHold, setComplianceHold] = useState<{
    title: string;
    documents: string[];
  } | null>(null);
  const [isProducing, setIsProducing] = useState(false);
  const [productionPreview, setProductionPreview] = useState<{
    attachment: ProductionAttachment | null;
    signingRequested: boolean;
    taskDescription: string | null;
  } | null>(null);
  const [productionContext, setProductionContext] =
    useState<ProductionContext | null>(null);
  const productionInFlight = useRef(false);
  const productionRunId = useRef(0);
  const [productionSigningResult, setProductionSigningResult] =
    useState<ProductionSigningResultData | null>(null);
  const [paramOverrides, setParamOverrides] = useState<Record<string, string>>(
    {}
  );
  const contactId = selectedContact?.id;
  const jsonImportInputRef = useRef<HTMLInputElement>(null);
  const importContactIdRef = useRef(contactId);
  importContactIdRef.current = contactId;
  const [importingJson, setImportingJson] = useState(false);
  const [showJsonTextImport, setShowJsonTextImport] = useState(false);
  const [jsonImportText, setJsonImportText] = useState("");

  const { linkedContact, userProfile } = useKeycloak();

  const { fetchCompletedTask } = useGetCompletedProcessTask();
  const { fetchProducedDocuments } = useGetProducedDocuments();
  const { downloadAttachment } = useDownloadAttachment();

  const { loading, error, data, refetch, networkStatus } =
    useGetContactOverview(selectedContact?.id);

  const { data: advisorData, loading: advisorLoading } =
    useGetExtendedContactDetails(linkedContact);

  // --------------------------------------------
  // Securities list
  // --------------------------------------------
  const { data: securities, loading: securitiesLoading } =
    useGetSavedQuery2<QuerySecurity>("advPortalInstruments", {
      status: "ACTIVE",
      typeCode: "%",
      currencyCode: "%",
      securityName: "%",
      tag: "%Capguard-Yes%",
    });

  // --------------------------------------------
  // Profile data
  // --------------------------------------------
  const { data: profileData, loading: profileLoading } =
    useGetSavedQuery2<QueryProfile>(
      "advPortalProfile",
      { profileKey: `contact-${contactId}` },
      !contactId
    );

  // --------------------------------------------
  // Model portfolios
  // --------------------------------------------
  const { data: modelsData, loading: modelsLoading } =
    useGetSavedQuery2<QueryModelPosition>("advPortalModel", {});

  const selectedContactState = state.contacts.find((c) => c.id === contactId);
  const persistedOverrides = selectedContactState?.docProducerOverrides;
  const docStamperOverrides = useMemo(
    () => persistedOverrides?.profile ?? {},
    [persistedOverrides?.profile]
  );
  const docStamperSourceDocIds = useMemo(
    () => new Set(persistedOverrides?.sourceDocIds ?? []),
    [persistedOverrides?.sourceDocIds]
  );
  const docProdOutOutput = persistedOverrides?.docProdOut ?? null;
  const afterSignOutput =
    (docProdOutOutput?.afterSign as Record<string, unknown> | undefined) ??
    persistedOverrides?.afterSign ??
    null;
  const updateDocProducerOverrides = useCallback(
    (patch: Partial<DocProducerOverrides>) => {
      if (!contactId) return;
      const current = selectedContactState?.docProducerOverrides;
      dispatch({
        type: "SET_DOC_PRODUCER_OVERRIDES",
        payload: {
          contactId: String(contactId),
          overrides: {
            profile: patch.profile ?? current?.profile ?? {},
            sourceDocIds: patch.sourceDocIds ?? current?.sourceDocIds ?? [],
            afterSign:
              patch.afterSign !== undefined
                ? patch.afterSign
                : current?.afterSign ?? null,
            docProdOut:
              patch.docProdOut !== undefined
                ? patch.docProdOut
                : current?.docProdOut ?? null,
          },
        },
      });
    },
    [contactId, dispatch, selectedContactState?.docProducerOverrides]
  );
  const isLoading = loading || securitiesLoading || profileLoading;

  // Memoize documents list
  const documents = useMemo(() => {
    return selectedContactState?.documents?.documents || [];
  }, [selectedContactState]);
  const { ordersLocked, canChangeOrders } = useOrderLock(documents);
  const existingOrders = useMemo(
    () =>
      documents
        .filter((document) => document.docType === "order")
        .map((document) => {
          const raw = document.data as Record<string, unknown>;
          const newData = (raw.newData ?? raw) as Record<string, unknown>;
          const existData = (raw.existData ?? {}) as Record<string, unknown>;
          return {
            id: document.id,
            portfolioId: String(
              newData.dpPortfolioId ?? existData.dpPortfolioId ?? ""
            ),
            portType: String(newData.dpPortType ?? existData.dpPortType ?? ""),
            modelType: String(
              newData.dpModelType ?? existData.dpModelType ?? ""
            ),
            modelName: String(
              newData.dpModelTypeName ?? existData.dpModelTypeName ?? ""
            ),
          };
        }),
    [documents]
  );
  const additionalOrderAccountNumbers = useMemo(() => {
    const accountNumbers = new Set<string>();
    for (const document of documents) {
      if (document.docType !== "order") continue;
      const raw = document.data as Record<string, unknown>;
      const newData = (raw.newData ?? raw) as Record<string, unknown>;
      const existData = (raw.existData ?? {}) as Record<string, unknown>;
      const selectedAccount = String(
        newData.dpAccount ?? existData.dpAccount ?? ""
      );
      const accountValue =
        selectedAccount === "newAccount"
          ? String(newData.dpNewAccount ?? "")
          : selectedAccount;
      const normalized = accountValue.replace(/\D/g, "");
      if (normalized.length === 11) accountNumbers.add(normalized);
    }
    return Array.from(accountNumbers);
  }, [documents]);
  const [documentFilter, setDocumentFilter] = useState<
    "all" | "orders" | "other"
  >("all");
  const documentCounts = useMemo(
    () => ({
      all: documents.length,
      orders: documents.filter((doc) => doc.docType === "order").length,
      other: documents.filter((doc) => doc.docType !== "order").length,
    }),
    [documents]
  );
  const visibleDocuments = useMemo(
    () =>
      documentFilter === "all"
        ? documents
        : documents.filter((doc) =>
            documentFilter === "orders"
              ? doc.docType === "order"
              : doc.docType !== "order"
          ),
    [documentFilter, documents]
  );
  const documentSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [draggedDocumentId, setDraggedDocumentId] = useState<string | null>(
    null
  );
  const draggedDocument = documents.find((doc) => doc.id === draggedDocumentId);
  const handleDocumentDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setDraggedDocumentId(null);
      if (!over || active.id === over.id || !selectedContactState) return;
      const oldIndex = visibleDocuments.findIndex(
        (doc) => doc.id === active.id
      );
      const newIndex = visibleDocuments.findIndex((doc) => doc.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(visibleDocuments, oldIndex, newIndex);
      const visibleIds = new Set(visibleDocuments.map((doc) => doc.id));
      let index = 0;
      dispatch({
        type: "REORDER_DOCUMENTS",
        payload: {
          contactId: selectedContactState.contactId,
          documents: documents.map((doc) =>
            visibleIds.has(doc.id) ? reordered[index++] : doc
          ),
        },
      });
    },
    [dispatch, documents, selectedContactState, visibleDocuments]
  );

  const [docData, setDocData] = useState<DocProducerData | null>(null);
  const docDataRef = useRef<DocProducerData | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    docDataRef.current = docData;
  }, [docData]);

  // Reset form when selectedContact changes
  useEffect(() => {
    setShowJsonTextImport(false);
    setJsonImportText("");
    setDraggedDocumentId(null);
    setShowForm(false);
    setShowFormsPreview(false);
    setPreviewDocuments([]);
    setActiveQualityForm(null);
    setActiveLoadFormId(null);
    setShowUploader(false);
    setDocData(null);
    setProductionPreview(null);
    setProductionContext(null);
    setProductionSigningResult(null);
    productionRunId.current += 1;
    productionInFlight.current = false;
    setIsProducing(false);
    setShowProductionList(false);
    setComplianceHold(null);
    setParamOverrides({});
    setDocumentFilter("all");
  }, [selectedContact?.id]);

  // Auto-add contact to state when contact data is loaded
  useEffect(() => {
    if (!data && contactId && !loading) {
      refetch();
      return;
    }
    if (data?.contact && contactId) {
      const exists = state.contacts.some((c) => c.id === contactId);
      if (
        !exists &&
        data.contact &&
        data.contact.id === contactId &&
        advisorData?.contact
      ) {
        const newEntry: ContactEntry = {
          id: contactId,
          contactId: String(contactId),
          contact: data.contact,
          documents: {
            documents: [],
            contactData: data.contact,
          },
          modData: {},
        };
        dispatch({ type: "ADD_CONTACT", payload: newEntry });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    advisorData,
    loading,
    networkStatus,
    showForm,
    contactId,
    state.contacts,
    dispatch,
    refetch,
  ]);

  const handleDocumentClick = useCallback(
    (formKey: string, documentTitle: string, isProduction = false) => {
      setShowOrderForm(false);
      setActiveQualityForm(null);
      setDocData({
        formKey,
        title: documentTitle,
        toProduction: isProduction,
      });
      setShowForm(true);
    },
    []
  );

  const replaceContactDocuments = useCallback(
    (documents: {
      documents: DocProducerDocument[];
      [key: string]: object;
    }) => {
      if (contactId) {
        dispatch({
          type: "REPLACE_DOCUMENTS",
          payload: { contactId: String(contactId), documents },
        });
      }
    },
    [contactId, dispatch]
  );

  const fetchAttachmentAsBase64 = useCallback(
    async (taskId: string, attachmentName: string) => {
      const DOWNLOAD_ATTACHMENT_QUERY = gql`
        query GetTaskAttachment($taskId: String, $attachmentName: String) {
          task(taskId: $taskId) {
            attachment(name: $attachmentName)
          }
        }
      `;
      const { data } = await apolloClient.query<{
        task: { attachment: string | null } | null;
      }>({
        query: DOWNLOAD_ATTACHMENT_QUERY,
        variables: { taskId, attachmentName },
        fetchPolicy: "network-only",
      });
      return data?.task?.attachment ?? null;
    },
    [apolloClient]
  );

  const openProductionPreview = useCallback(
    (tasks: ProducedDocumentsTask["tasks"], signingRequested = false) => {
      const attachment =
        tasks
          .flatMap((task) =>
            (task.attachments ?? []).map((item) => ({
              taskId: task.taskId,
              name: getAttachmentName(item),
            }))
          )
          .find((item) => item.name.toLowerCase().endsWith(".pdf")) ?? null;
      const taskDescription =
        tasks.find((task) => task.taskId === attachment?.taskId)?.description ??
        null;
      setProductionPreview({ attachment, signingRequested, taskDescription });
      const task = signingRequested ? tasks[0] : undefined;
      setProductionSigningResult(
        task
          ? {
              taskId: task.taskId,
              title: task.title,
              createDate: task.createDate,
              attachment,
              signingLinks: parseSigningInfo(task.description),
            }
          : null
      );
      setShowForm(true);
    },
    []
  );

  const onFetchTask = useCallback(
    async (taskId: string, title: string, isProduction = false) => {
      const { data, networkStatus } = await fetchCompletedTask(taskId);
      const json = data?.processCompletedTasks.processTask[0];
      if (isProduction) {
        const processInstanceId = json.processInstanceId;
        const { data: prodData } = await fetchProducedDocuments(
          processInstanceId
        );
        if (prodData && prodData.tasks.length > 0) {
          openProductionPreview(prodData.tasks);
        }
        return;
      }
      // Ensure documents are of type DocProducerDocument
      const returnValues = json?.data?.returnValues;
      if (returnValues?.documents?.length > 0) {
        replaceContactDocuments(returnValues);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rawData = json?.data as any;
      const returnedDocProdOut =
        rawData?.docProdOut &&
        typeof rawData.docProdOut === "object" &&
        !Array.isArray(rawData.docProdOut)
          ? (rawData.docProdOut as Record<string, unknown>)
          : null;
      const returnedAfterSign = returnedDocProdOut?.afterSign;
      const importProfile:
        | Array<{ profKey: string; profVal: unknown }>
        | undefined = rawData?.docProdOut?.afterSign?.importProfile;
      const updateMainContact: Array<Record<string, unknown>> | undefined =
        rawData?.docProdOut?.afterSign?.updateMainContact;

      const profileUpdates = [
        ...(Array.isArray(importProfile)
          ? importProfile
              .filter((e) => e.profKey && e.profVal != null)
              .map((e) => [e.profKey, e.profVal] as const)
          : []),
        ...(Array.isArray(updateMainContact)
          ? updateMainContact.flatMap((contactUpdate) =>
              Object.entries(contactUpdate).filter(
                ([key, value]) => key && value != null
              )
            )
          : []),
      ];

      if (profileUpdates.length > 0) {
        const overrides = Object.fromEntries(
          profileUpdates.map(([key, value]) => [key, String(value)])
        );
        const sourceIds = new Set(
          (returnValues?.documents ?? []).map((d: { id: string }) => d.id)
        );
        updateDocProducerOverrides({
          profile: overrides,
          sourceDocIds: Array.from(sourceIds),
          docProdOut: returnedDocProdOut,
          ...(returnedAfterSign &&
          typeof returnedAfterSign === "object" &&
          !Array.isArray(returnedAfterSign)
            ? { afterSign: returnedAfterSign as Record<string, unknown> }
            : {}),
        });
      } else if (returnedDocProdOut) {
        updateDocProducerOverrides({
          docProdOut: returnedDocProdOut,
          ...(returnedAfterSign &&
          typeof returnedAfterSign === "object" &&
          !Array.isArray(returnedAfterSign)
            ? { afterSign: returnedAfterSign as Record<string, unknown> }
            : {}),
        });
      }

      // Documents returned from Kundeprofil/MIFID/KYC forms that should be
      // added to the production list, filled with the shared docStamperData.
      const signDetails = rawData?.docProdOut?.signDetails as
        | {
            documentsFromSecurity?: Array<{
              documentType: string;
              fileName: string;
            }>;
          }
        | undefined;
      const documentsFromSecurity = signDetails?.documentsFromSecurity ?? [];
      if (documentsFromSecurity.length > 0 && contactId) {
        // Replace legacy placeholders when completed profile documents arrive.
        for (const placeholder of documents.filter(
          (d) => d.docType === "kundeprofil"
        )) {
          dispatch({
            type: "REMOVE_DOCUMENT",
            payload: { contactId: String(contactId), taskId: placeholder.id },
          });
        }

        for (const item of documentsFromSecurity) {
          const documentType = getProfileDocumentType(item.documentType);
          const fileName =
            item.fileName || item.documentType || "profile-document";
          const timestamp = new Date().toISOString();
          dispatch({
            type: "ADD_DOCUMENT",
            payload: {
              contactId: String(contactId),
              document: {
                id: getProfileDocumentId(
                  item.documentType,
                  item.fileName,
                  timestamp
                ),
                docType: documentType,
                processName: item.documentType || documentType,
                desc: fileName,
                data: {
                  documentType: item.documentType || documentType,
                  fileName,
                },
              },
            },
          });
        }
      }
    },
    [
      fetchCompletedTask,
      fetchProducedDocuments,
      replaceContactDocuments,
      openProductionPreview,
      contactId,
      dispatch,
      documents,
      updateDocProducerOverrides,
    ]
  );

  const handleModalClose = useCallback(
    async (taskId: string, title: string, isProduction = false) => {
      await onFetchTask(taskId, title, isProduction);
      setDocData(null);
      if (!isProduction) {
        setShowForm(false);
        setProductionPreview(null);
      }
    },
    [onFetchTask]
  );

  const handleComplete = useCallback(
    (taskId: string) => {
      const currentDocData = docDataRef.current;
      if (currentDocData) {
        handleModalClose(
          taskId,
          currentDocData.title,
          currentDocData.toProduction
        );
      }
    },
    [handleModalClose]
  );

  // Runs the production process imperatively — no form UI shown.
  // A unique referenceId is embedded in the payload; the Flowable process stores
  // it as the task reference so we can find exactly that task afterwards.
  // isSigning=true adds signees to the payload for the signing flow.
  // docList is the ordered list from ProductionListView (after user drag-and-drop reordering).

  const handleStartProcess = useCallback(
    async (
      isSigning: boolean,
      signees: Signee[],
      formDocuments: DocumentWithVariables[],
      kundeprofil: QueryProfile[],
      previewContext?: ProductionContext,
      pdfBytes?: string
    ) => {
      if (productionInFlight.current) return false;
      if (isSigning && signees.length === 0) {
        toast.error("Velg minst én signatar.");
        return false;
      }
      productionInFlight.current = true;
      const runId = ++productionRunId.current;
      setIsProducing(true);
      setShowProductionList(false);
      const input: ProductionContext = {
        documents: formDocuments,
        kundeprofil,
        portfolios:
          previewContext?.portfolios ?? data?.contact?.portfolios ?? [],
        standardParams: previewContext?.standardParams ?? standardParams,
        afterSign: previewContext ? previewContext.afterSign : afterSignOutput,
      };
      setProductionContext(input);
      let processStarted = false;
      try {
        const referenceId = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
          /[xy]/g,
          (c) => {
            const r = crypto.getRandomValues(new Uint8Array(1))[0] & 15;
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
          }
        );

        // Fire the production process — returns null (no user task/form defined)
        await apolloClient.mutate({
          mutation: PRODUCTION_START_PROCESS,
          variables: {
            key: "dp_documentProduction",
            data: {
              standardParams: input.standardParams,
              ...(pdfBytes ? { pdfBytes } : {}),
              documents: input.documents,
              portfolios: input.portfolios,
              referenceId,
              isSigning,
              selectedContactId: contactId,
              afterSign: input.afterSign,
              kundeprofil: input.kundeprofil,
              ...(isSigning ? { signees } : {}),
            },
          },
        });
        processStarted = true;
        if (runId !== productionRunId.current) return true;
        setProductionSigningResult(null);
        if (isSigning) {
          setProductionPreview((current) =>
            current ? { ...current, signingRequested: true } : null
          );
        }

        // Fetch produced documents.
        // For signing: filter by "Portal-docProd for <contactName>", then pick the
        // most recent task with a createDate within 30 minutes of now.
        // For production: filter by the unique referenceId.
        const MAX_ATTEMPTS = 5;
        const INITIAL_DELAY_MS = 2000;
        const RETRY_DELAY_MS = 1500;

        const taskFilter = referenceId;

        await new Promise((r) => setTimeout(r, INITIAL_DELAY_MS));
        if (runId !== productionRunId.current) return processStarted;

        let targetTask: ProducedDocumentsTask["tasks"][0] | undefined;
        let prodData: ProducedDocumentsTask | undefined = undefined;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          const result = await fetchProducedDocuments(taskFilter, isSigning);
          if (runId !== productionRunId.current) return processStarted;
          const tasks = result.data?.tasks ?? [];

          if (isSigning) {
            // Pick the newest task created within the last 30 minutes
            const now = dayjs();
            const recent = tasks
              .filter((t) => {
                const d = dayjs(t.createDate);
                return d.isValid() && now.diff(d, "minute") <= 2;
              })
              .sort(
                (a, b) =>
                  dayjs(b.createDate).valueOf() - dayjs(a.createDate).valueOf()
              );
            if (recent.length > 0) {
              targetTask = recent[0];
              prodData = { tasks: recent };
              break;
            }
          } else {
            if (tasks.length > 0) {
              prodData = result.data;
              break;
            }
          }

          if (attempt < MAX_ATTEMPTS) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
          }
        }

        if (!prodData || prodData.tasks.length === 0) {
          toast.error("Kunne ikke hente produserte dokumenter");
          return processStarted;
        }

        // Compliance holds the production: no signing links, only attachments.
        if (
          isSigning &&
          targetTask &&
          /^compliance/i.test(targetTask.title ?? "")
        ) {
          setComplianceHold({
            title: targetTask.title,
            documents: (targetTask.attachments ?? [])
              .map(getAttachmentName)
              .filter(Boolean),
          });
          setProductionPreview(null);
          setShowForm(false);
          return true;
        }

        // Preview only the PDF; diagnostic attachments are not part of this view.
        const taskList =
          isSigning && targetTask ? [targetTask] : prodData.tasks;
        openProductionPreview(taskList, isSigning);
        if (isSigning) {
          for (const document of input.documents) {
            dispatch({
              type: "REMOVE_DOCUMENT",
              payload: { contactId: String(contactId), taskId: document.id },
            });
          }
        }
        return true;
      } catch (err) {
        if (runId !== productionRunId.current) return processStarted;
        console.error("Production error:", err);
        toast.error(
          processStarted
            ? "Prosessen er startet, men resultatet kunne ikke hentes."
            : "Produksjon feilet. Prøv igjen."
        );
        return processStarted;
      } finally {
        if (runId === productionRunId.current) {
          productionInFlight.current = false;
          setIsProducing(false);
        }
      }
    },
    [
      apolloClient,
      standardParams,
      contactId,
      fetchProducedDocuments,
      openProductionPreview,
      afterSignOutput,
      dispatch,
      data?.contact?.portfolios,
    ]
  );

  // Memoize contact values to prevent re-renders
  const contactValues = useMemo(
    () => state.contacts.find((c) => c.id === contactId),
    [state.contacts, contactId]
  );

  const handleProduceDocuments = useCallback(async () => {
    if (productionInFlight.current) return;
    const contact = contactValues?.contact;
    if (!contact) {
      toast.error("Velg en kunde før du produserer dokumenter.");
      return;
    }
    if (!documents.length) {
      toast.error("Legg til minst ett dokument før du starter produksjonen.");
      return;
    }
    try {
      const activePortfolios = (contact.portfolios ?? []).filter(
        (portfolio) => portfolio.status === "A"
      );
      const payload = buildDocumentListWithVariables(
        contact,
        advisorData?.contact,
        documents,
        modelsData ?? [],
        securities ?? [],
        profileData,
        activePortfolios,
        [],
        docProdOutOutput
      );
      setStandardParams(payload.standardParams);
      setKundeprofil(payload.kundeprofil);
      await handleStartProcess(
        false,
        [],
        payload.documents,
        payload.kundeprofil,
        {
          ...payload,
          afterSign: afterSignOutput,
          portfolios: data?.contact?.portfolios ?? [],
        }
      );
    } catch (error) {
      console.error("Could not prepare document production:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Kunne ikke klargjøre dokumentene for forhåndsvisning."
      );
    }
  }, [
    contactValues,
    documents,
    advisorData?.contact,
    modelsData,
    securities,
    profileData,
    docProdOutOutput,
    afterSignOutput,
    data?.contact?.portfolios,
    handleStartProcess,
  ]);

  const availableSigningSignees = useMemo<Signee[]>(() => {
    const contact = contactValues?.contact;
    if (contact?.juridical?.code === "Company") {
      return (contact.representatives ?? [])
        .filter((representative) => representative.contactId?.startsWith("F"))
        .map(({ id, contactId, name }) => ({
          id,
          contactId,
          name,
        }));
    }
    return contact && String(contact.contactId ?? contact.id).startsWith("F")
      ? [
          {
            id: Number(contact.id),
            contactId: String(contact.contactId ?? contact.id),
            name: contact.name,
          },
        ]
      : [];
  }, [contactValues]);

  const isCompany = contactValues?.contact?.juridical?.code === "Company";
  const getKundeprofilUpdateType = (document: DocProducerDocument) => {
    const documentData = document.data as Record<string, unknown>;
    const updateType = documentData.updateType;
    if (updateType === "1" || updateType === "2" || updateType === "3") {
      return updateType;
    }

    const searchableText =
      `${document.docType} ${document.processName} ${document.desc}`.toLowerCase();
    if (
      document.docType === "MIFID" ||
      searchableText.includes("mifid") ||
      searchableText.includes("investorprofil")
    ) {
      return "2";
    }
    if (document.docType === "KYC" || searchableText.includes("aml")) {
      return "3";
    }
    if (
      document.docType === "kundeprofil" ||
      document.docType === "mifidForm" ||
      document.docType === "amlForm" ||
      searchableText.includes("kundeprofil")
    ) {
      return "1";
    }
    return null;
  };
  const hasSuitabilityDocument = ordersLocked;
  const hasMifid = documents.some(
    (document) => getKundeprofilUpdateType(document) === "2"
  );
  const hasKyc = documents.some(
    (document) => getKundeprofilUpdateType(document) === "3"
  );
  const hasKundeprofilDocument = documents.some(
    (document) => getKundeprofilUpdateType(document) === "1"
  );
  const hasLeiDocument = documents.some(
    (document) =>
      document.docType === "lei" || document.docType === "docleiOrder"
  );

  const handleDeleteDocument = useCallback(
    (document: DocProducerDocument) => {
      if (document.docType === "order" && !canChangeOrders()) return;
      if (contactId) {
        dispatch({
          type: "REMOVE_DOCUMENT",
          payload: { contactId: String(contactId), taskId: document.id },
        });
        const clearsOverrides =
          document.docType === "kundeprofil" ||
          document.docType === "documentFromSecurity" ||
          document.docType === "mifidForm" ||
          document.docType === "amlForm" ||
          docStamperSourceDocIds.has(document.id);
        if (clearsOverrides && Object.keys(docStamperOverrides).length > 0) {
          updateDocProducerOverrides({
            profile: {},
            sourceDocIds: [],
            docProdOut: null,
            afterSign: null,
          });
        }
      }
    },
    [
      contactId,
      dispatch,
      docStamperOverrides,
      docStamperSourceDocIds,
      updateDocProducerOverrides,
      canChangeOrders,
    ]
  );

  const handleEditDocument = useCallback(
    (doc: DocProducerDocument) => {
      if (doc.docType === "order") {
        if (!canChangeOrders()) return;
        setEditingQualityDoc(null);
        setActiveQualityForm(null);
        setEditingOrderDoc(doc);
        setShowOrderFormNew(true);
      } else if (
        doc.docType === "meetingMinutes" ||
        doc.docType === "openCustomerInstruction"
      ) {
        setEditingOrderDoc(null);
        setShowOrderForm(false);
        setShowOrderFormNew(false);
        setEditingQualityDoc(doc);
        setActiveQualityForm(doc.docType);
      } else {
        return;
      }
      setShowForm(true);
    },
    [canChangeOrders]
  );

  // Pending orders derived from the order documents in the list
  const pendingOrders = useMemo<PendingOrder[]>(() => {
    return documents
      .filter((d) => d.docType === "order")
      .map((d) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const nd = ((d.data as any).newData ?? {}) as Record<string, unknown>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ed = ((d.data as any).existData ?? {}) as Record<string, unknown>;
        const modelType = String(nd.dpModelType ?? "");
        const buyRowsForNames =
          (nd.buyOrders as Array<{ instrumentName?: string }>) ?? [];
        const sellRowsForNames =
          (nd.sellOrders as Array<{
            name?: string;
            instrumentName?: string;
          }>) ?? [];
        const instrumentNames = Array.from(
          new Set(
            [
              ...buyRowsForNames.map((r) => (r.instrumentName ?? "").trim()),
              ...sellRowsForNames.map((r) =>
                (r.instrumentName ?? r.name ?? "").trim()
              ),
            ].filter(Boolean)
          )
        );
        const manualOrderRows = buyRowsForNames
          .map((r) => {
            const instrumentCode = String(
              (r as { instrumentCode?: string }).instrumentCode ?? ""
            ).trim();
            const name = (
              r.instrumentName ??
              (r as { instrumentCode?: string }).instrumentCode ??
              ""
            ).trim();
            const amountNok = parseOrderAmount(
              (r as { amountInput?: string | number }).amountInput
            );
            const class1 = String(
              (r as { class1code?: string }).class1code ?? ""
            ).toUpperCase();
            const class2 = String(
              (r as { class2code?: string }).class2code ?? ""
            ).toUpperCase();
            const isStock = class1 === "E" || class2 === "S" || class2 === "EF";
            const security = findSecurityForOrderRow(
              securities,
              instrumentCode,
              name
            );
            return { instrumentName: name, amountNok, isStock, security };
          })
          .filter((r) => r.instrumentName && r.amountNok > 0);

        // For NOMODEL orders, compute stock fraction from the actual buy order lines.
        // A buy row is "stock" if class1code === "E" (equities) OR class2code is "S"/"EF"
        // (same logic as buildSuitOrder.ts resolveIsStock).
        let stockFraction: number | undefined;
        if (!modelType || modelType === "NOMODEL") {
          const buyRows =
            (nd.buyOrders as
              | Array<{
                  instrumentName?: string;
                  class1code?: string;
                  class2code?: string;
                  amountInput?: string;
                }>
              | undefined) ?? [];
          const filledRows = buyRows.filter(
            (r) =>
              r.instrumentName ||
              (r as { instrumentCode?: string }).instrumentCode
          );
          const totalBuy = filledRows.reduce(
            (s, r) => s + parseOrderAmount(r.amountInput),
            0
          );
          if (totalBuy > 0) {
            const stockBuy = filledRows
              .filter(
                (r) =>
                  String(r.class1code ?? "").toUpperCase() === "E" ||
                  String(r.class2code ?? "").toUpperCase() === "S" ||
                  String(r.class2code ?? "").toUpperCase() === "EF"
              )
              .reduce((s, r) => s + parseOrderAmount(r.amountInput), 0);
            stockFraction = stockBuy / totalBuy;
          } else {
            stockFraction = 0;
          }
        }

        return {
          portfolioId: String(nd.dpPortfolioId ?? ""),
          portfolioName: String(
            nd.dpPortfolioName ?? ed.dpPortfolioName ?? "Portefolje"
          ),
          portfolioType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
          isNew: !(ed.isExisting as boolean),
          portType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
          amountToInvest: Number(nd.dpAmountToInvest ?? 0),
          oneOffAmount: Number(nd.dpOnOffAmount ?? 0),
          sumAvCash: Number(ed.dpSumAvCash ?? 0),
          sumInsValue: Number(ed.dpSumInsValue ?? 0),
          withdrawal: Number(nd.dpWithdrawal ?? 0),
          modelType,
          modelTypeName: String(nd.dpModelTypeName ?? ""),
          instrumentNames,
          manualOrderRows,
          managementFeePct: Number(nd.dpManagementFee ?? 0),
          subscriptionFeePct: Number(nd.dpOnOffPercentage ?? 0),
          feeType: String(nd.dpFeeType ?? ""),
          stockFraction,
        };
      });
  }, [documents, securities]);

  const getFormDocumentsJson = useCallback(() => {
    const contact = contactValues?.contact;
    if (!contact || !selectedContactState) {
      throw new Error("Kundedata er ikke tilgjengelig");
    }

    const output = buildDocumentListWithVariables(
      contact,
      advisorData?.contact,
      documents,
      modelsData ?? [],
      securities ?? [],
      profileData,
      [],
      pendingOrders,
      docProdOutOutput
    );
    return JSON.stringify(
      {
        ...output,
        portfolios: data?.contact?.portfolios ?? [],
        restore: createDocumentRestore(selectedContactState),
      },
      documentJsonReplacer,
      2
    );
  }, [
    advisorData?.contact,
    contactValues,
    documents,
    modelsData,
    pendingOrders,
    securities,
    profileData,
    docProdOutOutput,
    data?.contact?.portfolios,
    selectedContactState,
  ]);

  const handleImportDocumentsJson = async (source: File | string) => {
    if (!contactId || !selectedContactState || productionInFlight.current) {
      toast.error(
        "Vent til kunden er lastet og eventuell produksjon er ferdig."
      );
      return;
    }
    const importContactId = contactId;
    setImportingJson(true);
    try {
      const restored = parseDocumentRestore(
        typeof source === "string" ? source : await source.text(),
        String(importContactId),
        selectedContactState.contact.portfolios ?? [],
        securities ?? []
      );
      if (
        importContactIdRef.current !== importContactId ||
        productionInFlight.current
      ) {
        throw new Error(
          "Valgt kunde eller produksjonsstatus er endret. Prøv importen igjen."
        );
      }
      if (
        !window.confirm(
          [
            `Erstatt alle dokumenter og ordre for valgt kunde med ${restored.documents.length} dokumenter fra JSON-importen? Dette kan ikke angres.`,
            ...(restored.warnings ?? []),
          ].join("\n\n")
        )
      )
        return;
      dispatch({
        type: "IMPORT_DOCUMENTS",
        payload: {
          contactId: String(importContactId),
          documents: restored.documents,
          modData: restored.modData,
          overrides: restored.overrides,
        },
      });
      setShowForm(false);
      setShowOrderForm(false);
      setShowOrderFormNew(false);
      setEditingOrderDoc(null);
      setEditingQualityDoc(null);
      setActiveQualityForm(null);
      setActiveLoadFormId(null);
      setShowUploader(false);
      setDocData(null);
      setShowFormsPreview(false);
      setPreviewDocuments([]);
      setShowProductionList(false);
      setProductionDocList([]);
      setProductionPreview(null);
      setProductionContext(null);
      setProductionSigningResult(null);
      setComplianceHold(null);
      setParamOverrides({});
      setStandardParams({});
      setKundeprofil([]);
      setDocumentFilter("all");
      setDraggedDocumentId(null);
      setShowJsonTextImport(false);
      setJsonImportText("");
      toast.success("Dokumenter og ordre er erstattet fra JSON-importen.");
      if (restored.warnings?.length) {
        toast.warning(restored.warnings.join(" "), { autoClose: false });
      }
    } catch (error) {
      console.error("Could not import document JSON:", error);
      toast.error(
        error instanceof Error ? error.message : "Kunne ikke importere JSON."
      );
    } finally {
      setImportingJson(false);
    }
  };

  const handleCopyFormDocumentsJson = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getFormDocumentsJson());
      toast.success("Dokument-JSON kopiert til utklippstavlen", {
        autoClose: 700,
        hideProgressBar: false,
      });
    } catch (error) {
      console.error("Could not copy document JSON:", error);
      toast.error("Kunne ikke kopiere dokument-JSON til utklippstavlen", {
        autoClose: 700,
        hideProgressBar: false,
      });
    }
  }, [getFormDocumentsJson]);

  const handleDownloadFormDocumentsJson = useCallback(() => {
    try {
      const blob = new Blob([getFormDocumentsJson()], {
        type: "application/json;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      try {
        link.href = url;
        link.download = `dokumenter-${dayjs().format(
          "YYYY-MM-DD-HHmmss"
        )}.json`;
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (error) {
      console.error("Could not download document JSON:", error);
      toast.error("Kunne ikke laste ned dokument-JSON");
    }
  }, [getFormDocumentsJson]);

  const handleDownloadAttachment = useCallback(
    async (taskId: string, attachmentName: string) => {
      try {
        const base64Data = await fetchAttachmentAsBase64(
          taskId,
          attachmentName
        );
        if (base64Data) {
          // Extract base64 content (remove data:application/pdf;base64, prefix if present)
          const base64Content = base64Data.includes(",")
            ? base64Data.split(",")[1]
            : base64Data;

          // Convert base64 to blob
          const byteCharacters = atob(base64Content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });

          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = attachmentName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Error downloading attachment:", error);
        toast.error("Could not download the document. Please try again.");
      }
    },
    [fetchAttachmentAsBase64]
  );

  const formValues: ContactEntry | undefined = useMemo(() => {
    if (
      !contactId ||
      !contactValues ||
      !securities ||
      !profileData ||
      !modelsData ||
      !advisorData
    )
      return undefined;
    return {
      id: contactId,
      contact: contactValues.contact,
      contactId: String(contactId),
      documents: {
        ...contactValues?.documents,
        avdData: advisorData.contact,
        faSec: securities,
        profile: buildProfileMap(profileData),
        faModels: modelsData,
      },
      modData: {},
    };
  }, [
    contactId,
    contactValues,
    securities,
    profileData,
    modelsData,
    advisorData,
  ]);

  // Effective profile map for SuitStatement: profileMap merged with user overrides
  const baseProfileMap = useMemo(
    () => buildProfileMap(profileData ?? []),
    [profileData]
  );
  const effectiveProfileMap = useMemo(
    () => ({ ...baseProfileMap, ...paramOverrides, ...docStamperOverrides }),
    [baseProfileMap, paramOverrides, docStamperOverrides]
  );
  // docStamperOverrides win over manual test-param overrides
  const effectiveParamOverrides = useMemo(
    () => ({ ...paramOverrides, ...docStamperOverrides }),
    [paramOverrides, docStamperOverrides]
  );
  const { range: recommendedEquityRange } = resolveRecommendedEquityRange(
    baseProfileMap,
    docStamperOverrides
  );
  const suitabilityBlockReason = profileLoading
    ? "Henter kundeavtale og anbefalt aksjeintervall"
    : !recommendedEquityRange
    ? "Kundeavtale mangler: anbefalt aksjeintervall må være registrert før du kan opprette egnethetsattest"
    : null;
  const canAddSuitabilityDocument = useCallback(() => {
    if (suitabilityBlockReason) {
      toast.error(suitabilityBlockReason);
      return false;
    }
    return true;
  }, [suitabilityBlockReason]);
  const handleParamOverrideChange = useCallback(
    (key: string, value: string) =>
      setParamOverrides((prev) => {
        if (value === "") {
          const next = { ...prev };
          delete next[key];
          return next;
        }
        return { ...prev, [key]: value };
      }),
    []
  );

  // Memoize FormView to prevent re-renders when dialog moves
  const formView = useMemo(() => {
    if (!showForm || !contactId) return null;

    const qualityData = editingQualityDoc?.data as
      | Record<string, unknown>
      | undefined;
    const qualityInitialData = qualityData
      ? ((qualityData.newData ?? qualityData) as Record<string, unknown>)
      : undefined;
    const saveQualityDocument = (document: DocProducerDocument) => {
      if (editingQualityDoc) {
        dispatch({
          type: "REORDER_DOCUMENTS",
          payload: {
            contactId: String(contactId),
            documents: documents.map((doc) =>
              doc.id === document.id ? document : doc
            ),
          },
        });
      } else {
        dispatch({
          type: "ADD_DOCUMENT",
          payload: { contactId: String(contactId), document },
        });
      }
    };

    // Meeting Minutes form
    if (activeQualityForm === "meetingMinutes") {
      return (
        <div className="h-full overflow-hidden px-4 pt-4">
          <MeetingMinutesForm
            key={editingQualityDoc?.id ?? "new-meetingMinutes"}
            initialData={qualityInitialData}
            contactName={data?.contact?.name ?? undefined}
            advisorName={advisorData?.contact?.name ?? undefined}
            onDocumentCreated={(data) => {
              saveQualityDocument({
                id:
                  editingQualityDoc?.id ??
                  `meeting_${new Date().toISOString()}`,
                docType: "meetingMinutes",
                processName:
                  editingQualityDoc?.processName ?? "Meeting Minutes",
                desc: editingQualityDoc?.desc ?? "Meeting Minutes",
                data: {
                  ...qualityData,
                  newData: { ...qualityInitialData, ...data },
                },
              });
            }}
            onClose={() => {
              setEditingQualityDoc(null);
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // Suitability form
    if (activeQualityForm === "suitability") {
      const activePortfolios = (
        contactValues?.contact?.portfolios ?? []
      ).filter((p) => p.status === "A");

      const pendingOrders: PendingOrder[] = documents
        .filter((d) => d.docType === "order")
        .map((d) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const nd = ((d.data as any).newData ?? {}) as Record<string, unknown>;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const ed = ((d.data as any).existData ?? {}) as Record<
            string,
            unknown
          >;
          const modelType = String(nd.dpModelType ?? "");
          const buyRows =
            (nd.buyOrders as
              | Array<{
                  instrumentName?: string;
                  instrumentCode?: string;
                  class1code?: string;
                  class2code?: string;
                  amountInput?: string | number;
                }>
              | undefined) ?? [];
          const sellRows =
            (nd.sellOrders as Array<{
              name?: string;
              instrumentName?: string;
            }>) ?? [];
          const instrumentNames = Array.from(
            new Set(
              [
                ...buyRows.map((r) => (r.instrumentName ?? "").trim()),
                ...sellRows.map((r) =>
                  (r.instrumentName ?? r.name ?? "").trim()
                ),
              ].filter(Boolean)
            )
          );
          const manualOrderRows = buyRows
            .map((r) => {
              const instrumentCode = String(
                (r as { instrumentCode?: string }).instrumentCode ?? ""
              ).trim();
              const name = (
                r.instrumentName ??
                (r as { instrumentCode?: string }).instrumentCode ??
                ""
              ).trim();
              const amountNok = parseOrderAmount(
                (r as { amountInput?: string | number }).amountInput
              );
              const class1 = String(
                (r as { class1code?: string }).class1code ?? ""
              ).toUpperCase();
              const class2 = String(
                (r as { class2code?: string }).class2code ?? ""
              ).toUpperCase();
              const isStock =
                class1 === "E" || class2 === "S" || class2 === "EF";
              const security = findSecurityForOrderRow(
                securities,
                instrumentCode,
                name
              );
              return { instrumentName: name, amountNok, isStock, security };
            })
            .filter((r) => r.instrumentName && r.amountNok > 0);
          const stockFraction =
            !modelType || modelType === "NOMODEL"
              ? computeNoModelStockFraction(buyRows)
              : undefined;
          return {
            serviceType: String(nd.dpServiceType ?? ""),
            portfolioId: String(nd.dpPortfolioId ?? ""),
            portfolioName: String(
              nd.dpPortfolioName ?? ed.dpPortfolioName ?? "Portefolje"
            ),
            portfolioType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
            isNew: !(ed.isExisting as boolean),
            portType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
            amountToInvest: Number(nd.dpAmountToInvest ?? 0),
            oneOffAmount: Number(nd.dpOnOffAmount ?? 0),
            sumAvCash: Number(ed.dpSumAvCash ?? 0),
            sumInsValue: Number(ed.dpSumInsValue ?? 0),
            withdrawal: Number(nd.dpWithdrawal ?? 0),
            modelType,
            modelTypeName: String(nd.dpModelTypeName ?? ""),
            instrumentNames,
            manualOrderRows,
            managementFeePct: Number(nd.dpManagementFee ?? 0),
            subscriptionFeePct: Number(nd.dpOnOffPercentage ?? 0),
            feeType: String(nd.dpFeeType ?? ""),
            stockFraction,
          };
        });

      return (
        <div className="h-full overflow-auto p-4">
          <SuitabilityForm
            profileData={profileData ?? []}
            portfolios={activePortfolios}
            pendingOrders={pendingOrders}
            modelsData={modelsData ?? []}
            onDocumentCreated={(data) => {
              if (!canAddSuitabilityDocument()) return;
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `suitability_${new Date().toISOString()}`,
                    docType: "suitabilityStatement",
                    processName: "Suitability",
                    desc: "Suitability",
                    data: {
                      newData: data,
                      existData: {
                        isExisting: false,
                      },
                    },
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // Suitability New form (with docType: docSuitabilityNew)
    if (activeQualityForm === "suitabilityNew") {
      const activePortfolios = (
        contactValues?.contact?.portfolios ?? []
      ).filter((p) => p.status === "A");

      const pendingOrders: PendingOrder[] = documents
        .filter((d) => d.docType === "order")
        .map((d) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const nd = ((d.data as any).newData ?? {}) as Record<string, unknown>;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const ed = ((d.data as any).existData ?? {}) as Record<
            string,
            unknown
          >;
          const modelType = String(nd.dpModelType ?? "");
          const buyRows =
            (nd.buyOrders as
              | Array<{
                  instrumentName?: string;
                  instrumentCode?: string;
                  class1code?: string;
                  class2code?: string;
                  amountInput?: string | number;
                }>
              | undefined) ?? [];
          const sellRows =
            (nd.sellOrders as Array<{
              name?: string;
              instrumentName?: string;
            }>) ?? [];
          const instrumentNames = Array.from(
            new Set(
              [
                ...buyRows.map((r) => (r.instrumentName ?? "").trim()),
                ...sellRows.map((r) =>
                  (r.instrumentName ?? r.name ?? "").trim()
                ),
              ].filter(Boolean)
            )
          );
          const manualOrderRows = buyRows
            .map((r) => {
              const instrumentCode = String(
                (r as { instrumentCode?: string }).instrumentCode ?? ""
              ).trim();
              const name = (
                r.instrumentName ??
                (r as { instrumentCode?: string }).instrumentCode ??
                ""
              ).trim();
              const amountNok = parseOrderAmount(
                (r as { amountInput?: string | number }).amountInput
              );
              const class1 = String(
                (r as { class1code?: string }).class1code ?? ""
              ).toUpperCase();
              const class2 = String(
                (r as { class2code?: string }).class2code ?? ""
              ).toUpperCase();
              const isStock =
                class1 === "E" || class2 === "S" || class2 === "EF";
              const security = findSecurityForOrderRow(
                securities,
                instrumentCode,
                name
              );
              return { instrumentName: name, amountNok, isStock, security };
            })
            .filter((r) => r.instrumentName && r.amountNok > 0);
          const stockFraction =
            !modelType || modelType === "NOMODEL"
              ? computeNoModelStockFraction(buyRows)
              : undefined;
          return {
            serviceType: String(nd.dpServiceType ?? ""),
            portfolioId: String(nd.dpPortfolioId ?? ""),
            portfolioName: String(
              nd.dpPortfolioName ?? ed.dpPortfolioName ?? "Portefolje"
            ),
            portfolioType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
            isNew: !(ed.isExisting as boolean),
            portType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
            amountToInvest: Number(nd.dpAmountToInvest ?? 0),
            oneOffAmount: Number(nd.dpOnOffAmount ?? 0),
            sumAvCash: Number(ed.dpSumAvCash ?? 0),
            sumInsValue: Number(ed.dpSumInsValue ?? 0),
            withdrawal: Number(nd.dpWithdrawal ?? 0),
            modelType,
            modelTypeName: String(nd.dpModelTypeName ?? ""),
            instrumentNames,
            manualOrderRows,
            managementFeePct: Number(nd.dpManagementFee ?? 0),
            subscriptionFeePct: Number(nd.dpOnOffPercentage ?? 0),
            feeType: String(nd.dpFeeType ?? ""),
            stockFraction,
          };
        });

      if (!contactValues?.contact) return null;

      return (
        <div className="h-full overflow-auto p-4">
          <SuitabilityNewForm
            contact={contactValues.contact}
            profileData={profileData ?? []}
            portfolios={activePortfolios}
            pendingOrders={pendingOrders}
            modelsData={modelsData ?? []}
            paramOverrides={effectiveParamOverrides}
            onDocumentCreated={(suitData) => {
              if (!canAddSuitabilityDocument()) return;
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `suitabilityNew_${new Date().toISOString()}`,
                    docType: "docSuitabilityNew",
                    processName: "Suitability",
                    desc: "Suitability",
                    data: {
                      newData: suitData,
                      existData: {
                        isExisting: false,
                      },
                    },
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // KYC Privat form
    if (activeQualityForm === "kycPriv") {
      return (
        <div className="h-full overflow-auto p-4">
          <KycPrivForm
            contact={data?.contact}
            profileData={profileData ?? []}
            onDocumentCreated={(data) => {
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `kycPriv_${new Date().toISOString()}`,
                    docType: "customerAgreement",
                    processName: "KYC Privat",
                    desc: "KYC Privat",
                    data,
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // KYC Org form
    if (activeQualityForm === "kycOrg") {
      return (
        <div className="h-full overflow-auto p-4">
          <KycOrgForm
            contact={data?.contact}
            profileData={profileData ?? []}
            primaryAddress={data?.contact?.addresses?.[0]}
            onDocumentCreated={(data) => {
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `kycOrg_${new Date().toISOString()}`,
                    docType: "customerAgreement",
                    processName: "KYC Org",
                    desc: "KYC Org",
                    data,
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // Open Customer Instruction form
    if (activeQualityForm === "openCustomerInstruction") {
      return (
        <div className="h-full overflow-hidden px-4 pt-4">
          <OpenCustomerInstructionForm
            key={editingQualityDoc?.id ?? "new-openCustomerInstruction"}
            initialData={qualityInitialData}
            onDocumentCreated={(data) => {
              saveQualityDocument({
                id:
                  editingQualityDoc?.id ??
                  `openCustomerInstruction_${new Date().toISOString()}`,
                docType: "openCustomerInstruction",
                processName:
                  editingQualityDoc?.processName ?? "Open customer instruction",
                desc: editingQualityDoc?.desc ?? "Open customer instruction",
                data: {
                  ...qualityData,
                  newData: { ...qualityInitialData, ...data },
                },
              });
            }}
            onClose={() => {
              setEditingQualityDoc(null);
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // Advisory Against form
    if (activeQualityForm === "advisoryAgainst") {
      return (
        <div className="h-full overflow-auto p-4">
          <AdvisoryAgainstForm
            onDocumentCreated={(data) => {
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `advisoryAgainst_${new Date().toISOString()}`,
                    docType: "advisoryAgainst",
                    processName: "Advisory against",
                    desc: "Advisory against",
                    data: {
                      newData: data,
                    },
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    if (ordersLocked && (showOrderFormNew || showOrderForm)) {
      return (
        <div role="alert" className="p-4 text-sm text-muted-foreground">
          {ORDER_LOCK_REASON}
        </div>
      );
    }

    // OrderFormNew
    if (showOrderFormNew && contactValues) {
      const portfolios = contactValues.contact?.portfolios ?? [];
      const rawEditingData = editingOrderDoc?.data as
        | Record<string, unknown>
        | undefined;
      const editingData = rawEditingData
        ? {
            ...((rawEditingData.existData as Record<string, unknown>) ?? {}),
            ...((rawEditingData.newData as Record<string, unknown>) ?? {}),
          }
        : undefined;

      return (
        <div className="h-full overflow-hidden px-4 pt-4">
          <OrderFormNew
            portfolios={portfolios}
            existingOrders={existingOrders}
            isCompany={isCompany}
            classification2Code={contactValues.contact?.classification2?.code}
            classification3Code={contactValues.contact?.classification3?.code}
            securities={securities || []}
            modelsData={modelsData || []}
            additionalAccountNumbers={additionalOrderAccountNumbers}
            initialData={editingData}
            documentId={editingOrderDoc?.id}
            onDocumentCreated={(data, docId) => {
              if (!canChangeOrders()) return;
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: docId ?? `order_${new Date().toISOString()}`,
                    docType: "order",
                    processName: "Order",
                    desc:
                      ((data.newData as Record<string, unknown>)
                        ?.dpPortfolioName as string) || "Order",
                    data,
                  },
                },
              });
            }}
            onClose={() => {
              setShowOrderFormNew(false);
              setShowForm(false);
              setEditingOrderDoc(null);
            }}
          />
        </div>
      );
    }

    // Custom OrderForm
    if (showOrderForm && contactValues) {
      const portfolios = contactValues.contact?.portfolios ?? [];
      const rawEditingData = editingOrderDoc?.data as
        | Record<string, unknown>
        | undefined;
      const editingData = rawEditingData
        ? {
            ...((rawEditingData.existData as Record<string, unknown>) ?? {}),
            ...((rawEditingData.newData as Record<string, unknown>) ?? {}),
          }
        : undefined;

      return (
        <div className="h-full overflow-auto px-4 pt-4">
          <OrderForm
            portfolios={portfolios}
            isCompany={isCompany}
            securities={securities || []}
            initialData={editingData}
            documentId={editingOrderDoc?.id}
            onDocumentCreated={(data, documentId) => {
              if (!canChangeOrders()) return;
              if (documentId) {
                // Replace the existing document (same id)
                dispatch({
                  type: "ADD_DOCUMENT",
                  payload: {
                    contactId: String(contactId),
                    document: {
                      id: documentId,
                      docType: "order",
                      processName: "Order",
                      desc:
                        ((data.newData as Record<string, unknown>)
                          ?.dpPortfolioName as string) || "Order",
                      data,
                    },
                  },
                });
              } else {
                dispatch({
                  type: "ADD_DOCUMENT",
                  payload: {
                    contactId: String(contactId),
                    document: {
                      id: `order_${new Date().toISOString()}`,
                      docType: "order",
                      processName: "Order",
                      desc:
                        ((data.newData as Record<string, unknown>)
                          ?.dpPortfolioName as string) || "Order",
                      data,
                    },
                  },
                });
              }
            }}
            onClose={() => {
              setShowOrderForm(false);
              setShowForm(false);
              setEditingOrderDoc(null);
            }}
          />
        </div>
      );
    }

    // Generic Formio form loaded via useLoadForm — Order or any future form type
    if (activeLoadFormId !== null && contactValues) {
      return (
        <LoadFormRunner
          profileData={profileData || []}
          formId={activeLoadFormId}
          contactValues={contactValues}
          securities={securities || []}
          onDocumentCreated={(doc) => {
            if (doc.docType === "order" && !canChangeOrders()) return;
            if (
              (doc.docType === "suitabilityStatement" ||
                doc.docType === "docSuitabilityNew") &&
              !canAddSuitabilityDocument()
            )
              return;
            dispatch({
              type: "ADD_DOCUMENT",
              payload: { contactId: String(contactId), document: doc },
            });
          }}
          onClose={() => {
            setActiveLoadFormId(null);
            setShowForm(false);
          }}
        />
      );
    }

    // Otherwise show the form
    if (!docData || !contactValues || !formValues) return null;

    const viewValues = docData.toProduction
      ? { productionDocList }
      : { ...formValues, ...docData.extraValues };

    return (
      <FormView
        values={viewValues}
        formKey={docData.formKey}
        header={docData.title}
        onComplete={handleComplete}
        inpData={docData.inpData}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showForm,
    showOrderFormNew,
    showOrderForm,
    ordersLocked,
    canChangeOrders,
    canAddSuitabilityDocument,
    activeQualityForm,
    profileData,
    effectiveParamOverrides,
    activeLoadFormId,
    docData,
    contactId,
    contactValues,
    dispatch,
    productionDocList,
    editingOrderDoc,
    editingQualityDoc,
    documents,
    modelsData,
    additionalOrderAccountNumbers,
    existingOrders,
  ]);

  const handleCancelForm = useCallback(() => {
    setShowForm(false);
    setShowOrderForm(false);
    setShowOrderFormNew(false);
    setActiveQualityForm(null);
    setActiveLoadFormId(null);
    setShowUploader(false);
    setShowFormsPreview(false);
    setPreviewDocuments([]);
    setDocData(null);
    setProductionPreview(null);
    setProductionContext(null);
    setComplianceHold(null);
    setProductionDocList([]);
    setKundeprofil([]);
    setStandardParams({});
    setShowProductionList(false);
    setEditingOrderDoc(null);
    setEditingQualityDoc(null);
    setIsProducing(false);
    setProductionSigningResult(null);
  }, []);

  const handleUploadClick = useCallback(() => {
    setShowUploader(true);
    setShowForm(false);
    setDocData(null);
  }, []);

  const handleSubmitUploadedDocuments = useCallback(
    (files: UploadedDocument[]) => {
      if (contactId) {
        files.forEach((fileObj) => {
          const newDoc: DocProducerDocument = {
            id: fileObj.file.name + "_" + new Date().toISOString(),
            docType: "uploaded",
            processName: "Uploaded document",
            desc: fileObj.file.name,
            data: fileObj,
          };
          dispatch({
            type: "ADD_DOCUMENT",
            payload: { contactId: String(contactId), document: newDoc },
          });
        });
      }
      setShowUploader(false);
    },
    [contactId, dispatch]
  );

  const { access } = useKeycloak();
  const isAdmin = access.isAdmin;

  const isProductionFlowActive =
    showFormsPreview || showProductionList || isProducing || !!complianceHold;

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header with document type buttons */}
      <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Tooltip
            arrow
            describeChild
            title={ordersLocked ? ORDER_LOCK_REASON : ""}
          >
            <span
              className="inline-flex"
              tabIndex={ordersLocked ? 0 : undefined}
            >
              <Button
                onClick={() => {
                  if (!canChangeOrders()) return;
                  setEditingOrderDoc(null);
                  setShowOrderFormNew(true);
                  setShowForm(true);
                }}
                loading={isLoading}
                disabled={
                  !selectedContact ||
                  ordersLocked ||
                  isLoading ||
                  showForm ||
                  showUploader ||
                  isProductionFlowActive
                }
                variant="outline"
                size="sm"
                className={ordersLocked ? "pointer-events-none" : undefined}
              >
                Order
              </Button>
            </span>
          </Tooltip>
          <SelectButton
            label="Quality"
            loading={isLoading}
            disabled={
              !selectedContact ||
              isLoading ||
              showForm ||
              showUploader ||
              isProductionFlowActive
            }
            items={[
              {
                label: "Meeting Minutes",
                description: "Logg av møte med kunde",
                icon: <ClipboardList size={14} />,
                onSelect: () => {
                  setEditingQualityDoc(null);
                  setActiveQualityForm("meetingMinutes");
                  setShowForm(true);
                },
              },
              {
                label: "LEI Blankett",
                description: !isCompany
                  ? "Kun tilgjengelig for selskaper"
                  : hasLeiDocument
                  ? "Det finnes allerede en LEI-blankett"
                  : "Opprett LEI-blankett for selskapet",
                icon: <FileText size={14} />,
                disabled: !isCompany || hasLeiDocument,
                onSelect: () => {
                  if (!isCompany || hasLeiDocument || !contactId) return;
                  dispatch({
                    type: "ADD_DOCUMENT",
                    payload: {
                      contactId: String(contactId),
                      document: {
                        id: `lei_${new Date().toISOString()}`,
                        docType: "docleiOrder",
                        processName: "LEI Blankett",
                        desc: "LEI Blankett",
                        data: {},
                      },
                    },
                  });
                },
              },
              {
                label: "Suitability",
                description: hasSuitabilityDocument
                  ? "Slett eksisterende egnethetsattest før du oppretter en ny"
                  : suitabilityBlockReason ??
                    "Vurder egnethet med rådgivers kommentar",
                icon: <Scale size={14} />,
                disabled:
                  hasSuitabilityDocument || suitabilityBlockReason !== null,
                onSelect: () => {
                  if (hasSuitabilityDocument) return;
                  if (!canAddSuitabilityDocument()) return;
                  setActiveQualityForm("suitabilityNew");
                  setShowForm(true);
                },
              },
              {
                label: "Open customer instruction",
                description: "Åpen kundeordreinstruks",
                icon: <FileCheck2 size={14} />,
                onSelect: () => {
                  setEditingQualityDoc(null);
                  setActiveQualityForm("openCustomerInstruction");
                  setShowForm(true);
                },
              },
              {
                label: "Advisory against",
                description: "Dokumenter råd gitt mot kundens ønske",
                icon: <Ban size={14} />,
                onSelect: () => {
                  setActiveQualityForm("advisoryAgainst");
                  setShowForm(true);
                },
              },
            ]}
          />
          <SelectButton
            label="Kundeprofil"
            disabled={
              !selectedContact ||
              isLoading ||
              showForm ||
              showUploader ||
              isProductionFlowActive
            }
            items={["1", "2", "3"].map((updateType) => {
              const selContactName = data?.contact?.name ?? "";
              const textMap: Record<string, string> = {
                "1": `Du er invitert hit for å fylle ut en kundeavtale for ${selContactName}. Prosesen vil omfatte både en formell kundeavtale med lovpålagte AML-spørsmål, i tillegg til en MIFID2-profil.`,
                "2": `Du er invitert hit for å oppdatere MIFID2-profilen til ${selContactName}.`,
                "3": `Du er invitert hit for å oppdatere kundeavtale med lovpålagte AML-spørsmål til ${selContactName}.`,
              };
              const labelMap: Record<string, string> = {
                "1": "Kundeprofil",
                "2": "MIFID2-profil",
                "3": "AML-spørsmål",
              };
              const descMap: Record<string, string> = {
                "1": "Begge – AML-spørsmål og MIFID2-profil",
                "2": "Oppdater investorprofil og risikovurdering",
                "3": "Oppdater kundeavtale med lovpålagte AML-spørsmål",
              };
              const iconMap: Record<string, React.ReactNode> = {
                "1": <Users size={14} />,
                "2": <TrendingUp size={14} />,
                "3": <ShieldCheck size={14} />,
              };
              const itemDisabled =
                updateType === "1"
                  ? hasKundeprofilDocument || hasMifid || hasKyc
                  : updateType === "2"
                  ? hasKundeprofilDocument || hasMifid
                  : hasKundeprofilDocument || hasKyc;
              const disabledDescription = hasKundeprofilDocument
                ? "Slett eksisterende Kundeprofil før du oppretter en ny"
                : updateType === "1" && (hasMifid || hasKyc)
                ? "Kan bare velge MIFID eller AML når ett av dem allerede finnes"
                : updateType === "2" && hasMifid
                ? "Slett eksisterende MIFID2-profil før du oppretter en ny"
                : updateType === "3" && hasKyc
                ? "Slett eksisterende AML-dokument før du oppretter en ny"
                : descMap[updateType];
              return {
                label: labelMap[updateType],
                description: disabledDescription,
                icon: iconMap[updateType],
                disabled: itemDisabled,
                onSelect: () => {
                  if (itemDisabled) return;
                  setShowOrderForm(false);
                  setActiveQualityForm(null);
                  setDocData({
                    formKey: "capguard-kycVer2",
                    title: labelMap[updateType],
                    toProduction: false,
                    extraValues: {
                      selContactId: contactId,
                      selContactName,
                      updateType,
                    },
                    inpData: {
                      userProfile,
                      type: "newCustomer",
                      loadType: "docProd",
                      selContactId: contactId,
                      selContactName,
                      updateType,
                      text: textMap[updateType] ?? "",
                    },
                  });
                  setShowForm(true);
                },
              };
            })}
          />
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
          <Button
            onClick={handleUploadClick}
            disabled={
              !selectedContact ||
              isLoading ||
              showForm ||
              showUploader ||
              isProductionFlowActive
            }
            variant="outline"
            size="sm"
            icon={<Upload size={16} />}
            iconPosition="start"
          >
            Upload
          </Button>
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
          <Button
            disabled={
              !selectedContact ||
              isLoading ||
              showForm ||
              showUploader ||
              isProductionFlowActive ||
              documents.length === 0
            }
            onClick={() => void handleProduceDocuments()}
            variant="default"
            size="sm"
            icon={<File size={16} />}
          >
            Produce documents
          </Button>
        </div>
        {(showForm ||
          showUploader ||
          showFormsPreview ||
          showProductionList ||
          isProducing ||
          !!complianceHold) && (
          <Button
            icon={<X className="size-3" />}
            onClick={handleCancelForm}
            disabled={isProducing}
            variant="outline"
            size="sm"
          >
            Cancel
          </Button>
        )}
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-hidden">
        {!selectedContact ? (
          <div className="h-full flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <Info
                size={48}
                className="mx-auto mb-4 text-gray-400 dark:text-gray-600"
                strokeWidth={1.5}
              />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                No Contact Selected
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please select a contact from the list to create and manage
                documents.
              </p>
            </div>
          </div>
        ) : showForm && productionSigningResult ? (
          <ProductionSigningResult
            key={`${contactId}-${productionSigningResult.taskId}`}
            result={productionSigningResult}
            fetchAttachment={fetchAttachmentAsBase64}
            onDownload={handleDownloadAttachment}
          />
        ) : showForm && productionPreview ? (
          <ProductionPreview
            key={contactId}
            attachment={productionPreview.attachment}
            fetchAttachment={fetchAttachmentAsBase64}
            onDownload={handleDownloadAttachment}
            signees={availableSigningSignees}
            defaultSignee={!isCompany ? availableSigningSignees[0] : undefined}
            isSending={isProducing}
            signingRequested={productionPreview.signingRequested}
            taskDescription={productionPreview.taskDescription}
            onSendToSigning={
              productionContext
                ? (signees, pdfBytes) =>
                    handleStartProcess(
                      true,
                      signees,
                      productionContext.documents,
                      productionContext.kundeprofil,
                      productionContext,
                      pdfBytes
                    )
                : undefined
            }
          />
        ) : showForm ? (
          <div className="h-full">{formView}</div>
        ) : isProducing ? (
          <div className="h-full flex flex-col items-center justify-center gap-8 px-8">
            {/* Animated icon */}
            <LogoAnimation size={56} />

            {/* Text */}
            <div className="text-center space-y-2">
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                Producing documents
              </h3>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                This usually takes a few seconds…
              </p>
            </div>

            {/* Step indicators */}
            <div className="flex flex-col gap-2.5 w-full max-w-xs">
              {[
                { label: "Starting production process", delay: "0ms" },
                { label: "Generating documents", delay: "400ms" },
                { label: "Fetching produced files", delay: "800ms" },
              ].map((step, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 animate-pulse"
                  style={{ animationDelay: step.delay }}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {step.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className="w-full max-w-xs h-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-400 dark:bg-blue-500 rounded-full"
                style={{
                  width: "60%",
                  animation: "progress-indeterminate 2s ease-in-out infinite",
                }}
              />
            </div>

            <style>{`
              @keyframes progress-indeterminate {
                0%   { transform: translateX(-100%); width: 60%; }
                50%  { transform: translateX(80%);   width: 60%; }
                100% { transform: translateX(200%);  width: 60%; }
              }
            `}</style>
          </div>
        ) : complianceHold ? (
          <ComplianceHoldView
            title={complianceHold.title}
            documents={complianceHold.documents}
            onClose={handleCancelForm}
          />
        ) : showFormsPreview ? (
          <FormsPreviewView
            documents={previewDocuments}
            onBack={handleCancelForm}
            onProduce={(orderedDocuments) =>
              handleStartProcess(false, [], orderedDocuments, kundeprofil)
            }
          />
        ) : showProductionList ? (
          <ProductionListView
            productionDocList={productionDocList}
            kundeprofil={kundeprofil}
            onProduce={(_signees, orderedDocuments, kundeprofil) =>
              handleStartProcess(false, [], orderedDocuments, kundeprofil)
            }
          />
        ) : showUploader ? (
          <DocumentUploader onSubmit={handleSubmitUploadedDocuments} />
        ) : (
          <div className="flex h-full flex-col overflow-hidden lg:flex-row">
            {/* Left: document list */}
            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto bg-gray-50/40 p-5 dark:bg-gray-900/30">
              <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-white shadow-sm dark:bg-gray-100 dark:text-gray-900">
                    <FileText size={16} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold leading-tight text-gray-900 dark:text-gray-100">
                      Dokumenter
                    </h2>
                    <p className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400">
                      {documents.length}{" "}
                      {documents.length === 1
                        ? "dokument lagt"
                        : "dokumenter lagt"}{" "}
                      til for kunden
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1 rounded-xl border border-gray-200 bg-gray-100/80 p-1 dark:border-gray-700 dark:bg-gray-800">
                    {(
                      [
                        ["all", "Alle", documentCounts.all],
                        ["orders", "Ordrer", documentCounts.orders],
                        ["other", "Andre", documentCounts.other],
                      ] as const
                    ).map(([value, label, count]) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={documentFilter === value}
                        onClick={() => setDocumentFilter(value)}
                        className={cn(
                          "rounded-lg px-3 py-1.5 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                          documentFilter === value
                            ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100"
                            : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                        )}
                      >
                        {label}{" "}
                        <span className="ml-0.5 tabular-nums opacity-60">
                          {count}
                        </span>
                      </button>
                    ))}
                  </div>
                  <input
                    ref={jsonImportInputRef}
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    aria-label="Importer dokument-JSON"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = "";
                      if (file) void handleImportDocumentsJson(file);
                    }}
                  />
                  <Dialog
                    open={showJsonTextImport}
                    onClose={() => {
                      if (!importingJson) setShowJsonTextImport(false);
                    }}
                    fullWidth
                    maxWidth="md"
                    aria-labelledby="json-text-import-title"
                    sx={{ zIndex: 1500 }}
                  >
                    <DialogTitle id="json-text-import-title">
                      Importer JSON fra tekst
                    </DialogTitle>
                    <DialogContent>
                      <p className="mb-3 text-xs text-muted-foreground">
                        Lim inn en eksport med gjenopprettingsdata for valgt
                        kunde. Dokumenter og ordre erstattes etter bekreftelse.
                      </p>
                      <textarea
                        aria-label="JSON som skal importeres"
                        autoFocus
                        value={jsonImportText}
                        onChange={(event) =>
                          setJsonImportText(event.target.value)
                        }
                        disabled={importingJson}
                        spellCheck={false}
                        placeholder="Lim inn JSON her..."
                        className="min-h-72 w-full rounded-lg border border-border bg-background p-3 font-mono text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <div className="mt-3 flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={importingJson}
                          onClick={() => setShowJsonTextImport(false)}
                        >
                          Avbryt
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={importingJson || !jsonImportText.trim()}
                          onClick={() =>
                            void handleImportDocumentsJson(jsonImportText)
                          }
                        >
                          Importer
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        icon={
                          <ChevronDown className="size-3" aria-hidden="true" />
                        }
                        iconPosition="end"
                        variant="outline"
                        size="sm"
                        disabled={
                          importingJson || isProducing || !selectedContactState
                        }
                      >
                        JSON
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="z-[1400] w-40">
                      <DropdownMenuItem
                        disabled={documents.length === 0}
                        onSelect={handleCopyFormDocumentsJson}
                      >
                        <Copy size={14} aria-hidden="true" />
                        Kopier
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={documents.length === 0}
                        onSelect={handleDownloadFormDocumentsJson}
                      >
                        <DownloadCloud size={14} aria-hidden="true" />
                        Last ned
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => jsonImportInputRef.current?.click()}
                      >
                        <Upload size={14} aria-hidden="true" />
                        Importer fil
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => setShowJsonTextImport(true)}
                      >
                        <ClipboardList size={14} aria-hidden="true" />
                        Importer tekst
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {isLoading ? (
                <div className="flex w-full items-center justify-center py-20">
                  <LogoAnimation size={50} variant="pulse" />
                </div>
              ) : documents.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/70 px-8 py-14 text-center dark:border-gray-700 dark:bg-gray-900/50">
                  <FileText
                    size={32}
                    className="mx-auto mb-3 text-gray-300 dark:text-gray-600"
                    strokeWidth={1.5}
                  />
                  <p className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Ingen dokumenter ennå
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Velg en dokumenttype eller last opp et vedlegg for å komme i
                    gang.
                  </p>
                </div>
              ) : visibleDocuments.length === 0 ? (
                <div className="flex min-h-40 items-center justify-center rounded-lg border border-dashed border-gray-300 text-center dark:border-gray-700">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Ingen dokumenter i denne visningen
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Velg Alle for å se hele dokumentlisten.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
                  <div
                    aria-hidden="true"
                    className="hidden grid-cols-[minmax(12rem,1.2fr)_6rem_minmax(0,2fr)_2.25rem] gap-x-3 border-b border-gray-100 bg-gray-50/80 pl-11 pr-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:bg-gray-800/40 dark:text-gray-400 lg:grid"
                  >
                    <span>Navn</span>
                    <span>Type</span>
                    <span>Informasjon</span>
                    <span />
                  </div>
                  <DndContext
                    sensors={documentSensors}
                    collisionDetection={closestCenter}
                    onDragStart={({ active }) =>
                      setDraggedDocumentId(String(active.id))
                    }
                    onDragEnd={handleDocumentDragEnd}
                    onDragCancel={() => setDraggedDocumentId(null)}
                  >
                    <SortableContext
                      items={visibleDocuments.map((doc) => doc.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <ul aria-label="Dokumenter lagt til">
                        {visibleDocuments.map((doc) => (
                          <SortableDocumentItem
                            key={doc.id}
                            doc={doc}
                            disabledReason={
                              doc.docType === "order" && ordersLocked
                                ? ORDER_LOCK_REASON
                                : undefined
                            }
                            onDelete={handleDeleteDocument}
                            onEdit={handleEditDocument}
                          />
                        ))}
                      </ul>
                    </SortableContext>
                    {createPortal(
                      <DragOverlay dropAnimation={null} zIndex={1500}>
                        {draggedDocument ? (
                          <div
                            aria-hidden="true"
                            className="pointer-events-none relative rounded-xl border border-gray-200 bg-white pl-7 shadow-xl dark:border-gray-700 dark:bg-gray-900"
                          >
                            <GripVertical
                              size={16}
                              className="absolute left-3 top-6 text-gray-400"
                            />
                            <DocumentItem
                              doc={draggedDocument}
                              onDelete={handleDeleteDocument}
                              onEdit={handleEditDocument}
                            />
                          </div>
                        ) : null}
                      </DragOverlay>,
                      document.body
                    )}
                  </DndContext>
                </div>
              )}
            </div>

            <EquityProfileSummary
              profileMap={baseProfileMap}
              overrides={docStamperOverrides}
              loading={profileLoading}
            />

            {/* Right: SuitStatement parameter inspector
            {selectedContact && contactValues?.contact && isAdmin && (
              <SuitParamEditorPanel
                profileMap={baseProfileMap}
                overrides={paramOverrides}
                onChange={handleParamOverrideChange}
                isOrg={
                  contactValues.contact.juridical?.juridicalType !==
                  "PRIVATE_PERSON"
                }
              />
            )} */}
          </div>
        )}
      </div>
    </div>
  );
};

DocProducerContent.displayName = "DocProducerContent";

function resolveEquityMetric(
  profileMap: ProfileMap,
  overrides: Record<string, string>,
  keys: string[]
) {
  for (const [map, updated] of [
    [overrides, true],
    [profileMap, false],
  ] as const) {
    for (const key of keys) {
      const separator = key.lastIndexOf(".");
      const namespace = key.slice(0, separator + 1);
      const shortKey = key.slice(separator + 1);
      const typedKey = Object.keys(map).find(
        (candidate) =>
          candidate.startsWith(namespace) &&
          !candidate.slice(namespace.length).includes(".") &&
          candidate.endsWith(`_${shortKey}`)
      );
      const resolvedKey = typedKey ?? key;
      if (!Object.prototype.hasOwnProperty.call(map, resolvedKey)) continue;
      const raw = map[resolvedKey];
      if (!updated && (raw == null || raw === "")) continue;
      const missing = raw == null || String(raw).trim() === "";
      const number = missing
        ? null
        : typeof raw === "number"
        ? raw
        : typeof raw === "string"
        ? Number(raw.trim().replace(",", "."))
        : NaN;
      const valid =
        number !== null &&
        Number.isFinite(number) &&
        number >= 0 &&
        number <= 100;
      return {
        value: valid ? number : null,
        updated,
        invalid: !missing && !valid,
      };
    }
  }
  return { value: null, updated: false, invalid: false };
}

function resolveRecommendedEquityRange(
  profileMap: ProfileMap,
  overrides: Record<string, string>
) {
  const min = resolveEquityMetric(profileMap, overrides, [
    "overrideCGMinStockPart",
    "contact.cgProfiling.CGMinStockPart",
    "CGMinStockPart",
    "contact.profiling.recMinStockShare",
    "recMinStockShare",
  ]);
  const max = resolveEquityMetric(profileMap, overrides, [
    "overrideCGMaxStockPart",
    "contact.cgProfiling.CGMaxStockPart",
    "CGMaxStockPart",
    "contact.profiling.recMaxStockShare",
    "recMaxStockShare",
  ]);
  const range =
    min.value !== null && max.value !== null && min.value <= max.value
      ? { min: min.value, max: max.value }
      : null;
  return { min, max, range };
}

const EquityProfileSummary = ({
  profileMap,
  overrides,
  loading,
}: {
  profileMap: ProfileMap;
  overrides: Record<string, string>;
  loading: boolean;
}) => {
  const current = resolveEquityMetric(profileMap, overrides, [
    "overrideCurrentEquityPct",
    "contact.profiling.aumStocks",
    "aumStocks",
  ]);
  const { min, max, range } = resolveRecommendedEquityRange(
    profileMap,
    overrides
  );
  const system = resolveEquityMetric(profileMap, overrides, [
    "overrideCGSysStockPart",
    "contact.cgProfiling.CGSysStockPart",
    "CGSysStockPart",
  ]);
  const format = (metric: ReturnType<typeof resolveEquityMetric>) =>
    metric.invalid
      ? "Ugyldig verdi"
      : metric.value !== null
      ? `${metric.value.toLocaleString("nb-NO", {
          maximumFractionDigits: 1,
        })} %`
      : loading && !metric.updated
      ? "Laster..."
      : "Ikke oppgitt";
  const interval =
    min.invalid ||
    max.invalid ||
    (min.value !== null && max.value !== null && min.value > max.value)
      ? "Ugyldig intervall"
      : range
      ? `${range.min.toLocaleString("nb-NO", {
          maximumFractionDigits: 1,
        })}–${range.max.toLocaleString("nb-NO", {
          maximumFractionDigits: 1,
        })} %`
      : min.value !== null
      ? `Fra ${format(min)}`
      : max.value !== null
      ? `Opptil ${format(max)}`
      : format(min.updated ? min : max);
  const hasUpdates = [current, min, max, system].some(
    (metric) => metric.updated
  );

  return (
    <aside
      aria-label="Investeringsprofil"
      className="order-first w-full shrink-0 border-b border-gray-200 bg-slate-50/70 p-4 dark:border-gray-800 dark:bg-gray-950/30 lg:order-last lg:w-72 lg:overflow-y-auto lg:border-b-0 lg:border-l"
    >
      <div className="mb-4 flex items-center gap-2.5 text-gray-800 dark:text-gray-100">
        <span className="flex size-8 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm ring-1 ring-gray-200/70 dark:bg-gray-800 dark:text-blue-400 dark:ring-gray-700">
          <TrendingUp className="size-4" aria-hidden="true" />
        </span>
        <div>
          <h3 className="text-sm font-semibold">Investeringsprofil</h3>
          <p className="text-[10px] text-gray-500 dark:text-gray-400">
            Aksjeandel og anbefalt fordeling
          </p>
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm dark:border-gray-700/70 dark:bg-gray-900">
        <dl className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-1">
          {[
            {
              label: "Nåværende aksjeandel",
              value: format(current),
              updated: current.updated,
            },
            {
              label: "Aksjeintervall",
              value: interval,
              updated: min.updated || max.updated,
            },
            {
              label: "Systemanbefaling",
              value: format(system),
              updated: system.updated,
            },
          ].map((metric) => (
            <div
              key={metric.label}
              className="min-w-0 rounded-xl bg-slate-50 px-3 py-2.5 dark:bg-gray-800/50"
            >
              <dt className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                <span
                  aria-hidden="true"
                  className={cn(
                    "size-1.5 shrink-0 rounded-full",
                    metric.label === "Nåværende aksjeandel"
                      ? "bg-blue-500"
                      : metric.label === "Aksjeintervall"
                      ? "bg-indigo-400"
                      : "bg-teal-500"
                  )}
                />
                {metric.label}
              </dt>
              <dd className="mt-1 text-xl font-semibold tracking-tight tabular-nums text-gray-900 dark:text-gray-100">
                {metric.value}
              </dd>
              {metric.updated && (
                <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 dark:text-blue-300">
                  <Check size={11} aria-hidden="true" />
                  Oppdatert i DocProducer
                </span>
              )}
            </div>
          ))}
        </dl>
        {range && (
          <div
            className="mx-4 mb-4 rounded-xl border border-gray-100 px-3 py-3 dark:border-gray-800"
            aria-hidden="true"
          >
            <div className="relative mx-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800">
              <div
                className="absolute inset-y-0 min-w-px rounded-full bg-gradient-to-r from-blue-300 to-indigo-400 dark:from-blue-700 dark:to-indigo-500"
                style={{
                  left: `${range.min}%`,
                  width: `${range.max - range.min}%`,
                }}
              />
              {current.value !== null && (
                <div
                  className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-blue-600 shadow-sm dark:border-gray-900"
                  style={{ left: `${current.value}%` }}
                />
              )}
            </div>
            <div className="mt-1.5 flex justify-between text-[10px] text-gray-400">
              <span>0 %</span>
              <span>100 %</span>
            </div>
            <p className="mt-2 text-[10px] leading-relaxed text-gray-500 dark:text-gray-400">
              Fargefeltet viser aksjeintervallet. Prikken viser nåværende
              aksjeandel.
            </p>
          </div>
        )}
      </div>
      {hasUpdates && (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2.5 dark:border-blue-900/50 dark:bg-blue-950/20">
          <Info
            size={13}
            className="mt-0.5 shrink-0 text-blue-500"
            aria-hidden="true"
          />
          <p className="text-[11px] leading-relaxed text-blue-800 dark:text-blue-300">
            Oppdaterte verdier fra kundeavtalen prioriteres foran lagret profil.
          </p>
        </div>
      )}
    </aside>
  );
};

// ── SuitStatementPreviewPanel ────────────────────────────────────────────────

interface SuitStatementPreviewPanelProps {
  contact: Contact;
  baseProfileMap: ProfileMap;
  paramOverrides: Record<string, string>;
  activePorts: Portfolio[];
  pendingOrders: PendingOrder[];
  modelsData: QueryModelPosition[];
}

const suitStatementInstance = new SuitStatement();

// Module-level components — must NOT be defined inside render functions
// because React uses reference equality for component types; re-defining them
// on every render causes unmount→remount of their children (closing dropdowns).
const SPRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[9px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide leading-none">
      {label}
    </span>
    {children}
  </div>
);

const SPSection = ({ title }: { title: string }) => (
  <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 pb-0.5 mb-1 mt-2 first:mt-0">
    {title}
  </p>
);

// ── SuitParamEditorPanel ──────────────────────────────────────────────────────

interface SuitParamEditorPanelProps {
  profileMap: ProfileMap;
  overrides: Record<string, string>;
  onChange: (key: string, value: string) => void;
  isOrg: boolean;
}

const SuitParamEditorPanel = memo(
  ({ profileMap, overrides, onChange, isOrg }: SuitParamEditorPanelProps) => {
    /** Effective value: typed-prefix key has priority over legacy untyped key. */
    const val = (key: string): string => {
      if (key in overrides) return overrides[key];
      const typedOverride = Object.keys(overrides).find(
        (k) => k.endsWith(`_${key}`) && overrides[k] !== ""
      );
      if (typedOverride) return overrides[typedOverride];
      // typed-prefix variant takes priority (new data beats stale legacy data)
      const suffix = `_${key}`;
      for (const k of Object.keys(profileMap)) {
        if (k.startsWith("contact.cgProfiling.") && k.endsWith(suffix)) {
          const v = profileMap[k];
          if (v !== null && v !== undefined && v !== "") return String(v);
        }
      }
      // legacy fallback: short key or full cgProfiling key
      const direct =
        profileMap[key] ?? profileMap[`contact.cgProfiling.${key}`] ?? null;
      if (direct !== null && direct !== undefined && direct !== "")
        return String(direct);
      return "";
    };

    // Like val(), but used when the override key is synthetic (not a real profile attribute).
    const valOr = (overrideKey: string, profileKey: string): string => {
      if (overrideKey in overrides) return overrides[overrideKey];
      // val() already handles typed-prefix scan + legacy fallback for both short and full keys
      return val(overrideKey) || val(profileKey);
    };

    const overrideCount = Object.keys(overrides).length;

    const normalizeText = (s: string): string =>
      s
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[–—-]/g, "-")
        .replace(/[\/]/g, " ")
        .replace(/[()/%]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

    const FIELD_VALUE_ALIASES: Record<string, Record<string, string>> = {
      // Legacy org values from cgProfiling -> current option values.
      mifidIncomeStability: {
        stabil: "1",
        "helt stabil": "1",
        "noe variabel": "2",
        "noe variabel pluss minus 20": "2",
        "ganske variabel pluss minus 50": "3",
        "variabel uforutsigbar": "3",
        "variabel/uforutsigbar": "3",
        "helt variabel": "5",
      },
      mifidValueDeclineImpact: {
        lav: "1",
        moderat: "2",
        hoy: "3",
        stor: "3",
        "svaert hoy": "4",
        "svart hoy": "4",
        "svaert stor": "4",
      },
      mifidRiskToleranceVolatility: {
        lav: "1",
        moderat: "2",
        hoy: "3",
        offensiv: "4",
      },
      mifidMarketDropResponse: {
        "selge for a redusere tap": "Selge",
        selge: "Selge",
        "gjore ingenting og vente": "Vente",
        vente: "Vente",
        "vurdere a kjope mer": "Kjøpe mer / rebalansere",
        "rebalansere oke eksponering": "Kjøpe mer / rebalansere",
        "kjope mer rebalansere": "Kjøpe mer / rebalansere",
      },
      // HTML profile stores label text for these fields
      mifidEarliestWithdrawal: {
        "under ett ar": "1",
        "fra 1 5 ar": "2",
        "fra 1-5 ar": "2",
        "mer enn 5 ar": "3",
        "5 10 ar": "3",
        "mer enn 10 ar": "3",
        "mer enn 15 ar generasjonsforvaltning": "5",
      },
      mifidMainInvestmentPurpose: {
        kapitalbevaring: "1",
        "stabil avkastning": "2",
        "moderat vekst": "3",
        "langsiktig vekst": "4",
      },
      mifidLiquidityBuffer: {
        "under 250k": "<250K",
        "250k 1 mnok": "250K-1 MNOK",
        "250k-1 mnok": "250K-1 MNOK",
        "1 3 mnok": "1-3 MNOK",
        "1-3 mnok": "1-3 MNOK",
        "mer enn 3 mnok": ">3 MNOK",
      },
    };

    const toOptionValue = (
      fieldKey: string,
      raw: string,
      opts: readonly { label: string; value: string }[]
    ): string => {
      if (!raw) return "";
      if (opts.some((o) => o.value === raw)) return raw;

      const alias = FIELD_VALUE_ALIASES[fieldKey]?.[normalizeText(raw)];
      if (alias && opts.some((o) => o.value === alias)) return alias;

      const byLabel = opts.find((o) => o.label === raw);
      if (byLabel) return byLabel.value;

      const normRaw = normalizeText(raw);
      const fuzzy = opts.find(
        (o) =>
          normalizeText(o.value) === normRaw ||
          normalizeText(o.label) === normRaw
      );
      return fuzzy ? fuzzy.value : raw;
    };

    const selectCls =
      "w-full text-[10px] rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500";

    // Use a plain function (not a component) so React doesn't unmount/remount
    // the <select> on every re-render — that would close the dropdown.
    const mkSel = (
      k: string,
      opts: readonly { label: string; value: string }[]
    ) => (
      <select
        key={k}
        value={toOptionValue(k, val(k), opts)}
        onChange={(e) => onChange(k, e.target.value)}
        className={cn(
          selectCls,
          k in overrides ? "ring-1 ring-amber-400 border-amber-400" : ""
        )}
      >
        <option value="">—</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );

    // Select with fallback profile key when override key is synthetic.
    const mkSelOr = (
      overrideKey: string,
      profileKey: string,
      opts: readonly { label: string; value: string }[]
    ) => (
      <select
        key={overrideKey}
        value={toOptionValue(overrideKey, valOr(overrideKey, profileKey), opts)}
        onChange={(e) => onChange(overrideKey, e.target.value)}
        className={cn(
          selectCls,
          overrideKey in overrides
            ? "ring-1 ring-amber-400 border-amber-400"
            : ""
        )}
      >
        <option value="">—</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );

    // Row and Section are defined at module level (SPRow / SPSection) to keep
    // stable component identity across renders.

    /** Plain-function text input (same anti-pattern rule as mkSel). */
    const mkText = (k: string, placeholder?: string) => (
      <input
        key={k}
        type="text"
        value={val(k)}
        onChange={(e) => onChange(k, e.target.value)}
        placeholder={placeholder}
        className={cn(
          selectCls,
          k in overrides ? "ring-1 ring-amber-400 border-amber-400" : ""
        )}
      />
    );

    /** Plain-function number input. */
    const mkNum = (k: string, placeholder = "0") => (
      <input
        key={k}
        type="number"
        step="any"
        value={val(k)}
        onChange={(e) => {
          const raw = e.target.value;
          // Strip leading zeros (e.g. "0100" → "100"), but keep "0" and decimals intact
          const cleaned = raw.replace(/^0+(?=\d)/, "");
          onChange(k, cleaned);
        }}
        placeholder={placeholder}
        className={cn(
          selectCls,
          k in overrides ? "ring-1 ring-amber-400 border-amber-400" : ""
        )}
      />
    );

    return (
      <div className="w-72 xl:w-80 flex-shrink-0 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 overflow-y-auto">
        <div className="p-3 space-y-1.5">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
              <Pencil size={12} className="text-blue-500" />
              Test-parametre
            </h3>
            {overrideCount > 0 && (
              <button
                onClick={() =>
                  Object.keys(overrides).forEach((k) => onChange(k, ""))
                }
                className="text-[9px] text-amber-600 dark:text-amber-400 hover:underline"
                title="Nullstill alle overstyringer"
              >
                Nullstill ({overrideCount})
              </button>
            )}
          </div>

          {/* ── Risiko ── */}
          <SPSection title="Risiko" />
          <SPRow label="Komfort med svingninger">
            {mkSel(
              "mifidRiskToleranceVolatility",
              isOrg ? ORG_RISK_COMFORT_OPTIONS : RISK_COMFORT_OPTIONS
            )}
          </SPRow>
          <SPRow label="Reaksjon ved 30 % markedsfall">
            {mkSel("mifidMarketDropResponse", MARKET_DROP_RESPONSE_OPTIONS)}
          </SPRow>

          {/* ── Investorprofil ── */}
          <SPSection title="Investorprofil" />
          <SPRow
            label={isOrg ? "Investeringshorisont" : "Tidligst ta ut penger"}
          >
            {mkSelOr(
              "mifidInvestmentHorizon",
              "mifidEarliestWithdrawal",
              ORG_INVESTMENT_HORIZON_OPTIONS
            )}
          </SPRow>
          <SPRow label="Investeringsmål">
            {isOrg
              ? mkSelOr(
                  "mifidInvestmentGoal",
                  "mifidMainInvestmentPurpose",
                  ORG_INVESTMENT_GOAL_OPTIONS
                )
              : mkSel("mifidMainInvestmentPurpose", INVESTMENT_GOAL_OPTIONS)}
          </SPRow>
          <SPRow label="Likviditetsbehov">
            {mkSel(
              "mifidLiquidityNeed",
              isOrg ? ORG_LIQUIDITY_OPTIONS : LIQUIDITY_OPTIONS
            )}
          </SPRow>
          <SPRow label="Evne til å håndtere verdifall 20-30%">
            {mkSel("mifidValueDeclineImpact", VALUE_DECLINE_IMPACT_OPTIONS)}
          </SPRow>
          <SPRow label="Inntektsstabilitet">
            {mkSel("mifidIncomeStability", INCOME_STABILITY_OPTIONS)}
          </SPRow>

          {/* ── Instrumenter ── */}
          <SPSection title="Instrumenter  (K = kunnskap · E = erfaring)" />
          {MIFID_INSTRUMENT_ROWS.map((r) => (
            <div
              key={r.label}
              className="rounded-lg border border-gray-200 dark:border-gray-700 p-1.5 space-y-1"
            >
              <p className="text-[9px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {r.label}
              </p>
              <div className="grid grid-cols-2 gap-1">
                <SPRow label="Kunnskap">
                  {mkSel(r.knowledgeField, KNOWLEDGE_COLUMNS)}
                </SPRow>
                <SPRow label="Erfaring">
                  {mkSel(r.experienceField, EXPERIENCE_COLUMNS)}
                </SPRow>
                <SPRow label="Frekvens">
                  {mkSel(r.tradingFrequencyField, TRADING_FREQUENCY_COLUMNS)}
                </SPRow>
                <SPRow label="Periode">
                  {mkSel(r.periodField, PERIOD_COLUMNS)}
                </SPRow>
              </div>
            </div>
          ))}

          {/* ── Bakgrunn ── */}
          <SPSection title="Bakgrunn og erfaring" />
          <SPRow label="Relevant erfaring / utdanning">
            {mkText("mifidRelevantExperienceEducation", "Fri tekst...")}
          </SPRow>

          {/* ── ESG ── */}
          <SPSection title="ESG / Bærekraft" />
          <SPRow label="ESG-preferanser utover grunnleggende">
            {mkSel("mifidEsgPreference", ESG_PREF_OPTIONS)}
          </SPRow>
          <SPRow label="ESG-preferansetype">
            {mkText("mifidEsgPreferenceType", "Beskriv preferansetype...")}
          </SPRow>

          {/* ── Finansiell situasjon ── */}
          <SPSection title="Finansiell situasjon" />
          {isOrg ? (
            <>
              <SPRow label="Total netto formue (NOK)">
                {mkNum("mifidTotalNetWealth")}
              </SPRow>
              <SPRow label="Investerbare likvide midler (NOK)">
                {mkNum("mifidLiquidInvestableWealth")}
              </SPRow>
              <SPRow label="Planlagt kapitalbehov neste 2 år (NOK)">
                {mkNum("mifidLiquidityBuffer")}
              </SPRow>
            </>
          ) : (
            <>
              <SPRow label="Total netto formue (NOK)">
                {mkNum("mifidTotalNetWealth")}
              </SPRow>
              <SPRow label="Investerbare likvide midler (NOK)">
                {mkNum("mifidLiquidInvestableWealth")}
              </SPRow>
              <SPRow label="Brutto årsinntekt (NOK)">
                {mkNum("mifidGrossAnnualIncome")}
              </SPRow>
              <SPRow label="Planlagt kapitalbehov neste 2 år (NOK)">
                {mkNum("mifidLiquidityBuffer")}
              </SPRow>
            </>
          )}
          <SPRow label="Gjeldsbelastning">
            {mkSel("mifidDebtBurden", ORG_DEBT_BURDEN_OPTIONS)}
          </SPRow>

          {/* ── Aksjeandeler ── */}
          <SPSection title="Aksjeandeler %" />
          <SPRow label="Nåværende aksjeandel">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={valOr(
                "overrideCurrentEquityPct",
                "contact.profiling.aumStocks"
              )}
              onChange={(e) =>
                onChange("overrideCurrentEquityPct", e.target.value)
              }
              placeholder="0–100"
              className={cn(
                selectCls,
                "overrideCurrentEquityPct" in overrides
                  ? "ring-1 ring-amber-400 border-amber-400"
                  : ""
              )}
            />
          </SPRow>
          <SPRow label="Systemanbefalt aksjeandel">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={valOr(
                "overrideCGSysStockPart",
                "contact.cgProfiling.CGMaxStockPart"
              )}
              onChange={(e) =>
                onChange("overrideCGSysStockPart", e.target.value)
              }
              placeholder="0–100"
              className={cn(
                selectCls,
                "overrideCGSysStockPart" in overrides
                  ? "ring-1 ring-amber-400 border-amber-400"
                  : ""
              )}
            />
          </SPRow>
          <SPRow label="Min anbefalt aksjeandel">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={valOr(
                "overrideCGMinStockPart",
                "contact.cgProfiling.CGMinStockPart"
              )}
              onChange={(e) =>
                onChange("overrideCGMinStockPart", e.target.value)
              }
              placeholder="0–100"
              className={cn(
                selectCls,
                "overrideCGMinStockPart" in overrides
                  ? "ring-1 ring-amber-400 border-amber-400"
                  : ""
              )}
            />
          </SPRow>
          <SPRow label="Maks anbefalt aksjeandel">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={valOr(
                "overrideCGMaxStockPart",
                "contact.cgProfiling.CGMaxStockPart"
              )}
              onChange={(e) =>
                onChange("overrideCGMaxStockPart", e.target.value)
              }
              placeholder="0–100"
              className={cn(
                selectCls,
                "overrideCGMaxStockPart" in overrides
                  ? "ring-1 ring-amber-400 border-amber-400"
                  : ""
              )}
            />
          </SPRow>
        </div>
      </div>
    );
  }
);

SuitParamEditorPanel.displayName = "SuitParamEditorPanel";

const SuitStatementPreviewPanel = memo(
  ({
    contact,
    baseProfileMap,
    paramOverrides,
    activePorts,
    pendingOrders,
    modelsData,
  }: SuitStatementPreviewPanelProps) => {
    const { downloadReport, downloading } = useDownloadReport(
      `Capguard_SUIT_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`,
      false
    );
    const d = useMemo(
      () => {
        const stockParts = calcStockPartsFromPortfolios(
          activePorts,
          pendingOrders,
          modelsData
        );
        const suit = new AutoSuit(
          baseProfileMap,
          {
            ...paramOverrides,
            _stockCurrent: String(stockParts.current),
            _stockFin: String(stockParts.fin),
            _investmentAmount: String(
              // oneOffAmount is just the funding/withdrawal instruction for the
              // deposit, not an additional amount on top of amountToInvest.
              pendingOrders.reduce((sum, o) => sum + (o.amountToInvest ?? 0), 0)
            ),
          },
          contact,
          [],
          pendingOrders,
          modelsData
        );

        return {
          ...suit.toData(),
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        contact,
        baseProfileMap,
        paramOverrides,
        activePorts,
        pendingOrders,
        modelsData,
      ]
    );

    const handleDownloadReport = async () => {
      await downloadReport("Capguard - Suitability Juridical", { data: d });
    };

    return (
      <div className="w-72 xl:w-80 flex-shrink-0 border-l border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 overflow-y-auto">
        <div className="p-3">
          <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 mb-2">
            <FileCheck2 size={12} className="text-emerald-500" />
            MiFID Egnethetsattest
            <Button
              loading={downloading}
              onClick={handleDownloadReport}
              variant="icon"
              icon={<DownloadCloud className="size-3" />}
              size="xs"
            />
          </h3>
          <table className="w-full text-[10px] border-collapse">
            <tbody>
              {Object.entries(d).map(([key, value]) => {
                if (value === null || value === undefined || value === "")
                  return null;
                const display =
                  typeof value === "boolean"
                    ? value
                      ? "Ja"
                      : "Nei"
                    : String(value);
                return (
                  <tr
                    key={key}
                    className="border-b border-gray-100 dark:border-gray-800"
                  >
                    <td className="py-0.5 pr-2 text-gray-500 dark:text-gray-400 align-top whitespace-nowrap">
                      {key}
                    </td>
                    <td className="py-0.5 text-gray-800 dark:text-gray-200 font-medium text-right break-all">
                      {display}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);

SuitStatementPreviewPanel.displayName = "SuitStatementPreviewPanel";

// DocumentUploader component
interface DocumentUploaderProps {
  onSubmit: (files: UploadedDocument[]) => void;
}

const DocumentUploader = ({ onSubmit }: DocumentUploaderProps) => {
  const limitedFileTypes = ["pdf"];
  const maxTotalSize = 3 * 1024 * 1024;
  const [files, setFiles] = useState<UploadedDocument[] | null>(null);
  const [isOver, setIsOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const processingRef = useRef(false);

  const disableSubmit = isLoading || !files || files.length === 0;

  const addFiles = async (filesArray: File[]) => {
    if (processingRef.current || filesArray.length === 0) return;
    if (filesArray.some((file) => !/\.pdf$/i.test(file.name))) {
      toast.error("Only PDF files are allowed.");
      return;
    }
    const totalSize =
      (files ?? []).reduce((total, document) => total + document.file.size, 0) +
      filesArray.reduce((total, file) => total + file.size, 0);
    if (totalSize > maxTotalSize) {
      toast.error("The total size of all files must not exceed 3 MB.");
      return;
    }
    processingRef.current = true;
    setIsLoading(true);
    try {
      const base64Files = await Promise.all(
        filesArray.map(async (file) => {
          const base64 = await convertToBase64(file);
          return { file, stream: base64 };
        })
      );
      setFiles((prev) => [...(prev ?? []), ...base64Files]);
    } catch (cause) {
      toast.error(
        cause instanceof Error
          ? cause.message
          : "Could not read the selected PDF files."
      );
    } finally {
      processingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const droppedFiles = Array.from(event.dataTransfer.files);
    event.dataTransfer.clearData();
    setIsOver(false);
    await addFiles(droppedFiles);
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.onerror = () => {
        reject(new Error(`Could not read ${file.name}.`));
      };

      reader.readAsDataURL(file);
    });
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const filesArray = Array.from(e.target.files || []);
    e.target.value = "";
    await addFiles(filesArray);
  };

  return (
    <div className="p-5 h-full flex flex-col">
      <div className="flex flex-col gap-4 max-w-3xl mx-auto w-full">
        <div className="flex justify-between items-center">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Upload PDF Documents
          </h2>
          <input
            ref={fileInputRef}
            type="file"
            disabled={isLoading}
            accept={limitedFileTypes.map((t) => `.${t}`).join(",")}
            onChange={handleInputChange}
            className="hidden"
            multiple
          />
          <Button
            onClick={handleInputClick}
            disabled={isLoading}
            size="sm"
            variant="outline"
          >
            Select files
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          PDF only. Maximum total size: 3 MB.
          {files && files.length > 0 && (
            <>
              {" "}
              Selected:{" "}
              {(
                files.reduce(
                  (total, document) => total + document.file.size,
                  0
                ) /
                (1024 * 1024)
              ).toFixed(2)}{" "}
              MB.
            </>
          )}
        </p>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOver(false);
          }}
          onClick={(e) => {
            handleInputClick();
            e.stopPropagation();
          }}
          onDrop={(e) => handleDrop(e)}
          className={cn(
            "p-8 border-2 border-dashed rounded-xl transition-all cursor-pointer",
            isOver
              ? "border-primary bg-primary/5 dark:bg-primary/10"
              : "border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 bg-gray-50/50 dark:bg-gray-900/50"
          )}
        >
          {!files || files.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 text-center">
              <Upload
                size={48}
                className="text-gray-400 dark:text-gray-500"
                strokeWidth={1.5}
              />
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Drop files here or click to select
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Supports: {limitedFileTypes.join(", ").toUpperCase()}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {files.map((x, i) => {
                return (
                  <div
                    key={i}
                    onClick={(e) => e.stopPropagation()}
                    className="flex gap-3 px-3 py-2 items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                  >
                    <div className="flex-1 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center justify-center flex-shrink-0">
                        <File
                          size={16}
                          className="text-gray-600 dark:text-gray-400"
                        />
                      </div>
                      <div className="flex-1 flex flex-col min-w-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {x.file.name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {x.file.size > 1024 * 1024
                            ? `${(x.file.size / (1024 * 1024)).toFixed(2)} MB`
                            : `${(x.file.size / 1024).toFixed(2)} KB`}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFiles((prev) => {
                          if (prev) {
                            return prev.filter((_, index) => index !== i);
                          }
                          return null;
                        });
                      }}
                      className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-md transition-colors flex-shrink-0"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {files && files.length > 0 && (
          <div className="flex justify-end">
            <Button
              disabled={disableSubmit}
              loading={isLoading}
              loadingPosition="start"
              variant="default"
              size="sm"
              onClick={() => {
                if (files) {
                  onSubmit(files);
                  setFiles(null);
                }
              }}
            >
              {isLoading
                ? "Processing..."
                : `Upload ${files.length} ${
                    files.length === 1 ? "file" : "files"
                  }`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
